import { useState, useEffect, useCallback } from 'react';
import {
  FileText, Search, Plus, Eye, Download, RefreshCw, Delete, X,
  CheckCircle, Clock, XCircle, AlertTriangle, AlertCircle, Loader2, UserPlus,
  Building2, Globe, ChevronLeft, ChevronRight, FolderTree, FileSpreadsheet,
  Settings, Check, Upload, Save, Send, EyeOff, Trash2
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { gastosApi, emisoresApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import CabysExplorer from '../../components/CabysExplorer';
import DraggableModal from '../../components/DraggableModal';
import MonthRangePicker from '../../components/MonthRangePicker';
import PagadoPorModal from '../../components/gastos/PagadoPorModal';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
// Toolbar Button Component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
    info: "border border-cyan-400 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-500",
    success: "border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500",
    danger: "border border-red-400 text-red-500 hover:bg-red-50 hover:border-red-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-0.5" />
      <span>{label}</span>
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-10 bg-gray-300 mx-1" />;
}

const estadoConfig = {
  pendiente: { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  procesando: { label: 'Procesando', color: 'bg-blue-100 text-blue-800', icon: Clock },
  enviado: { label: 'Enviado', color: 'bg-blue-100 text-blue-800', icon: Clock },
  aceptado: { label: 'Aceptado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rechazado: { label: 'Rechazado', color: 'bg-red-100 text-red-800', icon: XCircle },
  error: { label: 'Error', color: 'bg-red-100 text-red-800', icon: AlertTriangle }
};

const tipoIdOptions = [
  { value: '01', label: 'Cedula Fisica' },
  { value: '02', label: 'Cedula Juridica' },
  { value: '03', label: 'DIMEX' },
  { value: '04', label: 'NITE' },
  { value: '05', label: 'Extranjero No Domiciliado' },
  { value: '06', label: 'No Contribuyente' }
];

const condicionVentaOptions = [
  { value: '01', label: 'Contado' },
  { value: '02', label: 'Credito' },
  { value: '03', label: 'Consignacion' },
  { value: '04', label: 'Apartado' },
  { value: '05', label: 'Arrendamiento' },
  { value: '99', label: 'Otros' }
];

const medioPagoOptions = [
  { value: '01', label: 'Efectivo' },
  { value: '02', label: 'Tarjeta' },
  { value: '03', label: 'Cheque' },
  { value: '04', label: 'Transferencia' },
  { value: '05', label: 'Recaudado terceros' },
  { value: '99', label: 'Otros' }
];

// Configuración de columnas
const COLUMN_CONFIG = {
  fecha: { label: 'Fecha', default: true, fixed: true },
  tipoDoc: { label: 'Tipo Doc.', default: true },
  numero: { label: 'No. Documento', default: true, fixed: true },
  numProveedor: { label: 'No. Factura Prov.', default: true },
  proveedor: { label: 'Proveedor', default: true, fixed: true },
  termino: { label: 'Término', default: true }, // Contado / Crédito (clickeable para corregir)
  moneda: { label: 'Moneda', default: true },
  gravado: { label: 'Gravado', default: true },
  iva: { label: 'IVA', default: true },
  total: { label: 'Total', default: true, fixed: true },
  tipoCambio: { label: 'T/C', default: false },
  baseCRC: { label: 'Base CRC', default: false },
  ivaCRC: { label: 'IVA CRC', default: false },
  cabys: { label: 'CABYS', default: false },
  estado: { label: 'Estado Hacienda', default: true },
};

const getDefaultColumns = () => {
  const storageKey = 'fec_columns';
  const defaults = Object.fromEntries(
    Object.entries(COLUMN_CONFIG).map(([key, config]) => [key, config.default])
  );

  const saved = localStorage.getItem(storageKey);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  }
  return defaults;
};

export default function FacturaCompraEmitida() {
  const { currentEmisor } = useAuthStore();
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sin paginación: trae todos los del período filtrado (límite defensivo 5000).
  // El sticky thead + scroll interno permite ver todas las filas sin paginar.
  const [pagination, setPagination] = useState({ page: 1, limit: 5000, total: 0, pages: 0 });
  const [searchTerm, setSearchTerm] = useState('');
  // Filtro de período unificado (MonthRangePicker). Default = mes actual.
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('FacturaCompraEmitida', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return {
      modo: 'mes',
      periodo: `${y}-${m}`,
      desde: `${y}-${m}-01`,
      hasta: `${y}-${m}-${String(last).padStart(2, '0')}`
    };
  });
  const [showModal, setShowModal] = useState(false);
  const [showImportarModal, setShowImportarModal] = useState(false);
  const [editFecData, setEditFecData] = useState(null); // FEC rechazada para editar y reenviar
  const [viewFecDetail, setViewFecDetail] = useState(null); // FEC detail modal
  const [corregirModal, setCorregirModal] = useState(null); // { factura, nueva: 'contado'|'credito' }
  const [pagadoPorModal, setPagadoPorModal] = useState(null); // { factura, pagadoPor, empleadoNombre, porcentajeEmpresa }
  const [corrigiendo, setCorrigiendo] = useState(false);
  const [corregirPlazo, setCorregirPlazo] = useState(30);
  const [viewPdfUrl, setViewPdfUrl] = useState(null);
  const [fecPdfUrl, setFecPdfUrl] = useState(null);       // PDF original embebido en el detalle
  const [fecPdfLoading, setFecPdfLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [catFec, setCatFec] = useState(null); // FEC a categorizar (IVA acreditable)
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [sortField, setSortField] = useState('numero');
  const [sortDir, setSortDir] = useState('desc');
  const [columnVisibility, setColumnVisibility] = useState(() => getDefaultColumns());
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    if (COLUMN_CONFIG[columnKey]?.fixed) return;
    setColumnVisibility(prev => {
      const updated = { ...prev, [columnKey]: !prev[columnKey] };
      localStorage.setItem('fec_columns', JSON.stringify(updated));
      return updated;
    });
  };

  // Get visible columns
  const visibleColumns = Object.keys(COLUMN_CONFIG).filter(colId => columnVisibility[colId]);

  const getDateRange = useCallback(() => {
    const r = {};
    if (filtroFecha?.desde) r.desde = filtroFecha.desde;
    if (filtroFecha?.hasta) r.hasta = filtroFecha.hasta;
    return r;
  }, [filtroFecha]);

  const fetchFacturas = useCallback(async () => {
    try {
      setLoading(true);
      const range = getDateRange();
      const response = await gastosApi.listFEC({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...range
      });
      setFacturas(response.data.data || []);
      setPagination(prev => ({ ...prev, ...response.data.pagination }));
    } catch (error) {
      console.error('Error fetching FEC:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, getDateRange]);

  useEffect(() => {
    fetchFacturas();
  }, [fetchFacturas]);

  // Cargar automáticamente el PDF original al abrir el detalle de una FEC
  useEffect(() => {
    let objectUrl = null;
    let cancelled = false;
    if (viewFecDetail?.id && viewFecDetail?.pdf_storage_path) {
      setFecPdfLoading(true);
      gastosApi.viewFECPdf(viewFecDetail.id)
        .then(res => {
          if (cancelled) return;
          objectUrl = URL.createObjectURL(res.data);
          setFecPdfUrl(objectUrl);
        })
        .catch(() => { if (!cancelled) toast.error('No se pudo cargar el PDF de la factura'); })
        .finally(() => { if (!cancelled) setFecPdfLoading(false); });
    } else {
      setFecPdfUrl(null);
    }
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [viewFecDetail]);

  // Auto-refresh para FECs en procesamiento
  useEffect(() => {
    if (!autoRefresh) return;

    const hasProcessing = facturas.some(f => f.estado === 'pendiente' || f.estado === 'procesando' || f.estado === 'enviado');
    // Recién aceptada: la compra compañera y su categorización de IVA se crean unos segundos
    // después; sin esto la lista se quedaba con el punto naranja hasta recargar (29-oct-2026)
    const recienAceptada = facturas.some(f => f.estado === 'aceptado'
      && (!f.compra_id || (parseInt(f.lineas_sin_categorizar) || 0) > 0)
      && Date.now() - new Date(f.created_at).getTime() < 10 * 60 * 1000);
    if (!hasProcessing && !recienAceptada) return;

    const interval = setInterval(async () => {
      // Check status for all processing FECs
      for (const factura of facturas.filter(f => f.estado === 'pendiente' || f.estado === 'procesando' || f.estado === 'enviado')) {
        try {
          await gastosApi.checkFECStatus(factura.id);
        } catch (e) {
          console.error('Error checking FEC status:', e);
        }
      }
      fetchFacturas();
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, [facturas, fetchFacturas, autoRefresh]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchFacturas();
  };

  const handleCheckStatus = async (factura) => {
    try {
      const response = await gastosApi.checkFECStatus(factura.id);
      toast.success(`Estado: ${response.data.data.estado}`);
      fetchFacturas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al verificar estado');
    }
  };

  const handleDownloadXML = async (factura) => {
    try {
      const response = await gastosApi.downloadFECXml(factura.id);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FEC-${factura.numero_consecutivo}.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Error al descargar XML');
    }
  };

  const handleDownloadXMLRespuesta = async (factura) => {
    try {
      const response = await gastosApi.downloadFECXmlRespuesta(factura.id);
      const blob = new Blob([response.data], { type: 'application/xml' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FEC-${factura.numero_consecutivo}-respuesta.xml`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('No hay respuesta de Hacienda disponible');
    }
  };

  const formatMoney = (amount, currency = 'CRC') => {
    const num = parseFloat(amount) || 0;
    if (currency === 'USD') {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Exportar Excel
  const exportarExcel = async () => {
    try {
      setExporting(true);
      const dateRange = getDateRange();
      const response = await gastosApi.exportFECExcel({ search: searchTerm, columns: visibleColumns.join(','), ...dateRange });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FEC_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
    } catch (error) {
      toast.error('Error al exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  // Exportar PDF
  const exportarPdf = async () => {
    try {
      setExporting(true);
      const response = await gastosApi.exportFECPdf({ search: searchTerm, columns: visibleColumns.join(',') });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `FEC_${dayjs().format('YYYY-MM-DD')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado');
    } catch (error) {
      toast.error('Error al exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  // Selección
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === facturas.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(facturas.map(f => f.id)));
    }
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  // Descargar XML de seleccionados
  const handleDownloadSelectedXml = async () => {
    const selected = facturas.filter(f => selectedIds.has(f.id));
    for (const factura of selected) {
      await handleDownloadXML(factura);
    }
  };

  // Descargar XML Respuesta de seleccionados
  const handleDownloadSelectedXmlRespuesta = async () => {
    const selected = facturas.filter(f => selectedIds.has(f.id) && f.estado_hacienda === 'aceptado');
    if (selected.length === 0) {
      toast.error('Seleccione FECs aceptadas para descargar respuesta');
      return;
    }
    for (const factura of selected) {
      await handleDownloadXMLRespuesta(factura);
    }
  };

  // Eliminar seleccionados
  const handleDeleteSelected = async () => {
    try {
      setDeleting(true);
      const ids = Array.from(selectedIds);
      for (const id of ids) {
        await gastosApi.deleteFEC(id);
      }
      toast.success(`${ids.length} FEC${ids.length > 1 ? 's' : ''} eliminada${ids.length > 1 ? 's' : ''}`);
      setSelectedIds(new Set());
      fetchFacturas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  const selectedFacturas = facturas.filter(f => selectedIds.has(f.id));
  const hasSelection = selectedIds.size > 0;
  // FEC seleccionadas con líneas de IVA sin categorizar (para el botón Categorizar, admite varias)
  const categorizables = selectedFacturas.filter(f => f.compra_id && (parseInt(f.lineas_sin_categorizar) || 0) > 0);
  const canCategorizar = categorizables.length > 0;

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 flex items-center justify-center gap-2 flex-wrap">
        <ToolbarButton
          icon={Plus}
          label="Nueva FEC"
          variant="primary"
          onClick={() => setShowModal(true)}
        />
        <ToolbarButton
          icon={Upload}
          label="Importar PDF"
          variant="info"
          onClick={() => setShowImportarModal(true)}
        />

        <ToolbarSeparator />

        <ToolbarButton
          icon={Download}
          label="XML FEC"
          variant="info"
          onClick={handleDownloadSelectedXml}
          disabled={!hasSelection}
        />
        <ToolbarButton
          icon={Download}
          label="Respuesta"
          variant="success"
          onClick={handleDownloadSelectedXmlRespuesta}
          disabled={!hasSelection}
        />
        <ToolbarButton
          icon={FolderTree}
          label={categorizables.length > 1 ? `Categorizar IVA (${categorizables.length})` : 'Categorizar IVA'}
          variant="primary"
          onClick={() => setCatFec(categorizables)}
          disabled={!canCategorizar}
        />
        <ToolbarButton
          icon={Delete}
          label="Eliminar"
          variant="danger"
          onClick={() => setShowDeleteModal(true)}
          disabled={!hasSelection || deleting}
        />

        <ToolbarSeparator />

        <ToolbarButton
          icon={FileSpreadsheet}
          label="Excel"
          variant="success"
          onClick={exportarExcel}
          disabled={exporting || facturas.length === 0}
        />
        <ToolbarButton
          icon={FileText}
          label="PDF"
          variant="danger"
          onClick={exportarPdf}
          disabled={exporting || facturas.length === 0}
        />

        {hasSelection && (
          <>
            <ToolbarSeparator />
            <span className="text-sm text-gray-600 px-2">
              {selectedIds.size} seleccionada{selectedIds.size > 1 ? 's' : ''}
            </span>
            <ToolbarButton
              icon={X}
              label="Limpiar"
              variant="default"
              onClick={clearSelection}
            />
          </>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
          <strong>Factura Electronica de Compra:</strong> Se emite cuando compras a proveedores que
          no pueden emitir factura electronica: regimen simplificado, proveedores extranjeros
          (servicios/intangibles), o no contribuyentes.
        </p>
      </div>

      {/* Search */}
      <div className="bg-white rounded-lg border p-4">
        <form onSubmit={handleSearch} className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Buscar por proveedor, numero..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <MonthRangePicker
            value={filtroFecha}
            onChange={(v) => { setFiltroFecha(v); setPagination(p => ({ ...p, page: 1 })); }}
            onRefresh={fetchFacturas}
          />
          <button type="submit" className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            Buscar
          </button>
          <label
            className="flex items-center gap-2 text-sm cursor-pointer whitespace-nowrap"
            title={autoRefresh
              ? (facturas.some(f => f.estado === 'pendiente' || f.estado === 'procesando' || f.estado === 'enviado') ? 'Auto-check activo (cada 5s)' : 'Auto-check habilitado (sin FECs procesando)')
              : 'Auto-check desactivado'
            }
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
            />
            <span className={`flex items-center gap-1 ${autoRefresh && facturas.some(f => f.estado === 'pendiente' || f.estado === 'procesando' || f.estado === 'enviado') ? 'text-green-600 font-medium' : 'text-gray-600'}`}>
              Auto
              {autoRefresh && facturas.some(f => f.estado === 'pendiente' || f.estado === 'procesando' || f.estado === 'enviado') && (
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              )}
            </span>
          </label>
          <button
            onClick={() => setShowColumnSelector(!showColumnSelector)}
            className={`p-1.5 border rounded-lg hover:bg-gray-100 ${showColumnSelector ? 'border-blue-500 bg-blue-50' : 'border-gray-300'}`}
            title="Configurar columnas"
          >
            <Settings className="h-4 w-4 text-gray-600" />
          </button>
        </form>
      </div>

      {/* Column Selector Dropdown */}
      {showColumnSelector && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowColumnSelector(false)} />
          <div className="absolute right-4 mt-1 bg-white rounded-lg shadow-lg border z-50 min-w-[200px]">
            <div className="px-4 py-2 border-b bg-gray-50">
              <span className="text-sm font-medium text-gray-700">Columnas visibles</span>
            </div>
            <div className="py-2 max-h-80 overflow-y-auto">
              {Object.entries(COLUMN_CONFIG).map(([key, config]) => (
                <button
                  key={key}
                  onClick={() => !config.fixed && toggleColumn(key)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-50 ${config.fixed ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                    config.fixed ? 'bg-gray-300 border-gray-300' :
                      (columnVisibility[key] ? 'bg-blue-600 border-blue-600' : 'border-gray-300')
                  }`}>
                    {(config.fixed || columnVisibility[key]) && <Check className="h-3 w-3 text-white" />}
                  </span>
                  <span className={config.fixed ? 'text-gray-500' : 'text-gray-700'}>{config.label}</span>
                  {config.fixed && <span className="text-xs text-gray-400 ml-auto">(fija)</span>}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Table — sticky thead + tfoot + scroll interno (sin paginación) */}
      <div className="bg-white rounded-lg border overflow-hidden flex flex-col h-[calc(var(--alto-contenido)-230px)]">

        <div className="overflow-auto flex-1 min-h-0">
          <table className="w-full">
            <thead className="bg-gray-50 border-b sticky top-0 z-10">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={facturas.length > 0 && selectedIds.size === facturas.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                </th>
                {visibleColumns.map(colId => {
                  const config = COLUMN_CONFIG[colId];
                  const isRight = ['gravado', 'iva', 'total', 'tipoCambio', 'baseCRC', 'ivaCRC'].includes(colId);
                  const isCenter = colId === 'estado';
                  const isSorted = sortField === colId;
                  return (
                    <th
                      key={colId}
                      onClick={() => { if (sortField === colId) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(colId); setSortDir('asc'); } }}
                      className={`px-4 py-3 text-xs font-medium cursor-pointer hover:bg-gray-100 select-none ${isRight ? 'text-right' : isCenter ? 'text-center' : 'text-left'} ${isSorted ? 'text-blue-600' : 'text-gray-500'}`}
                    >
                      {config.label} {isSorted ? (sortDir === 'asc' ? '▲' : '▼') : ''}
                    </th>
                  );
                })}
                <th className="px-2 py-3 text-xs font-medium text-gray-500 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="px-4 py-8 text-center text-gray-500">
                    Cargando...
                  </td>
                </tr>
              ) : facturas.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="px-4 py-8 text-center text-gray-500">
                    No hay facturas de compra emitidas
                  </td>
                </tr>
              ) : (
                [...facturas].sort((a, b) => {
                  const fieldMap = {
                    fecha: 'fecha_emision', numero: 'numero_consecutivo', numProveedor: 'numero_factura_proveedor',
                    proveedor: 'proveedor_nombre', moneda: 'moneda', gravado: 'total_gravado',
                    iva: 'total_impuesto', total: 'total_comprobante', estado: 'estado',
                    tipoCambio: 'tipo_cambio'
                  };
                  const key = fieldMap[sortField] || 'numero_consecutivo';
                  let va = a[key] ?? '', vb = b[key] ?? '';
                  if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
                  else { va = parseFloat(va) || 0; vb = parseFloat(vb) || 0; }
                  if (va < vb) return sortDir === 'asc' ? -1 : 1;
                  if (va > vb) return sortDir === 'asc' ? 1 : -1;
                  return 0;
                }).map((factura) => {
                  const estado = estadoConfig[factura.estado] || estadoConfig.procesando;
                  const isSelected = selectedIds.has(factura.id);

                  const renderCell = (colId) => {
                    switch (colId) {
                      case 'fecha':
                        return <span className="text-sm text-gray-900">{dayjs(factura.fecha_emision).format('DD/MM/YYYY')}</span>;
                      case 'tipoDoc':
                        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">FEC</span>;
                      case 'numero':
                        return <span className="text-sm text-gray-900 font-mono">{factura.numero_consecutivo}</span>;
                      case 'numProveedor':
                        return <span className="text-sm text-gray-500">{factura.numero_factura_proveedor || '-'}</span>;
                      case 'proveedor':
                        return (
                          <>
                            <div className="text-sm font-medium text-gray-900">{factura.proveedor_nombre}</div>
                            <div className="text-xs text-gray-500">{factura.proveedor_identificacion}</div>
                          </>
                        );
                      case 'termino': {
                        const esAceptada = factura.estado_hacienda === 'aceptado';
                        const yaCorregida = !!factura.condicion_corregida;
                        const puedeCorregir = esAceptada && !yaCorregida;
                        const esContado = factura.condicion_venta === '01';
                        const nueva = esContado ? 'credito' : 'contado';
                        // Quién pagó (empresa / empleado): se guarda en la compra de la FEC (compra_id)
                        const [pe, pctE, nombreE] = String(factura.compra_pagado_por || '').split('|');
                        const porEmpleado = pe === '1';
                        const abrePagadoPor = esAceptada && esContado && !!factura.compra_id;
                        return (
                          <span
                            className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
                              esContado ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                            } ${abrePagadoPor || puedeCorregir ? 'cursor-pointer hover:ring-2 hover:ring-amber-400' : ''}`}
                            title={porEmpleado ? `Pagada por ${nombreE} (empresa asume ${parseFloat(pctE)}%) · se reintegra en planilla`
                              : abrePagadoPor ? 'Click: quién la pagó / corregir condición'
                              : puedeCorregir ? 'Click para corregir condición' : yaCorregida ? 'Condición ya fue corregida' : 'Solo se puede corregir si está aceptada por Hacienda'}
                            onClick={abrePagadoPor
                              ? (e) => { e.stopPropagation(); setPagadoPorModal({ factura, pagadoPor: porEmpleado ? 'empleado' : 'empresa', empleadoNombre: nombreE || '', porcentajeEmpresa: pctE ? parseFloat(pctE) : 100 }); }
                              : puedeCorregir ? (e) => { e.stopPropagation(); setCorregirModal({ factura, nueva }); } : undefined}
                          >
                            {esContado ? 'Contado' : 'Crédito'}
                            {porEmpleado && ' · Empleado'}
                            {yaCorregida && ' ✓'}
                          </span>
                        );
                      }
                      case 'moneda':
                        return <span className="text-sm text-gray-900">{factura.moneda}</span>;
                      case 'gravado':
                        return <span className="text-sm text-gray-900">{formatMoney(factura.total_gravado, factura.moneda)}</span>;
                      case 'iva':
                        return <span className="text-sm text-gray-900">{formatMoney(factura.total_impuesto, factura.moneda)}</span>;
                      case 'total':
                        return <span className="text-sm font-medium text-gray-900">{formatMoney(factura.total_comprobante, factura.moneda)}</span>;
                      case 'tipoCambio':
                        return <span className="text-sm text-gray-500">{factura.tipo_cambio ? `₡${parseFloat(factura.tipo_cambio).toFixed(2)}` : '-'}</span>;
                      case 'baseCRC':
                        return <span className="text-sm text-gray-500">{formatMoney(factura.total_gravado_crc || 0, 'CRC')}</span>;
                      case 'ivaCRC':
                        return <span className="text-sm text-gray-500">{formatMoney(factura.total_impuesto_crc || 0, 'CRC')}</span>;
                      case 'cabys':
                        return <span className="text-xs font-mono text-gray-500 max-w-[120px] truncate block" title={factura.cabys_codes || '-'}>{factura.cabys_codes || '-'}</span>;
                      case 'estado':
                        return (
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${estado.color} ${factura.respuesta_hacienda ? 'cursor-help' : ''}`}
                            title={factura.respuesta_hacienda || ''}
                          >
                            <estado.icon size={12} />
                            {estado.label}
                          </span>
                        );
                      default:
                        return null;
                    }
                  };

                  return (
                    <tr
                      key={factura.id}
                      onClick={() => toggleSelect(factura.id)}
                      className={`cursor-pointer transition-colors ${isSelected ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}`}
                    >
                      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleSelect(factura.id)}
                            className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                          />
                          {/* Palpitante de categorización de IVA (igual que en Facturas Aceptadas) */}
                          {(!factura.compra_id || (parseInt(factura.lineas_sin_categorizar) || 0) > 0)
                            ? <span className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse shrink-0" title={`${parseInt(factura.lineas_sin_categorizar) || 0} línea(s) de IVA sin categorizar`} />
                            : <span className="w-2.5 h-2.5 rounded-full bg-green-500 shrink-0" title="IVA categorizado" />}
                        </div>
                      </td>
                      {visibleColumns.map(colId => {
                        const isRight = ['gravado', 'iva', 'total', 'tipoCambio', 'baseCRC', 'ivaCRC'].includes(colId);
                        const isCenter = colId === 'estado';
                        return (
                          <td
                            key={colId}
                            className={`px-4 py-3 ${isRight ? 'text-right' : isCenter ? 'text-center' : ''}`}
                          >
                            {renderCell(colId)}
                          </td>
                        );
                      })}
                      <td className="px-2 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-center">
                          <button
                            onClick={async () => {
                              try {
                                const res = await gastosApi.getFEC(factura.id);
                                setViewFecDetail(res.data?.data);
                              } catch { toast.error('Error al cargar FEC'); }
                            }}
                            className="flex items-center gap-1 px-2 py-1 border border-blue-400 text-blue-600 rounded-lg text-xs hover:bg-blue-50"
                          >
                            <Eye className="h-3.5 w-3.5" /> Ver
                          </button>
                          {factura.estado === 'rechazado' && (
                            <button
                              onClick={async () => {
                                try {
                                  const res = await gastosApi.getFEC(factura.id);
                                  setEditFecData(res.data?.data);
                                } catch { toast.error('Error al cargar FEC'); }
                              }}
                              className="flex items-center gap-1 px-2 py-1 border border-amber-400 text-amber-600 rounded-lg text-xs hover:bg-amber-50"
                            >
                              <RefreshCw className="h-3.5 w-3.5" /> Editar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {/* Footer con totales */}
            {facturas.length > 0 && (() => {
              // Calculate totals by currency
              const totalsByCurrency = facturas.reduce((acc, f) => {
                const moneda = f.moneda || 'CRC';
                const tc = parseFloat(f.tipo_cambio) || 1;
                if (!acc[moneda]) {
                  acc[moneda] = { gravado: 0, iva: 0, total: 0 };
                }
                acc[moneda].gravado += parseFloat(f.total_gravado) || 0;
                acc[moneda].iva += parseFloat(f.total_impuesto) || 0;
                acc[moneda].total += parseFloat(f.total_comprobante) || 0;

                // Always sum CRC equivalents
                const isCRC = moneda === 'CRC';
                acc.baseCRC = (acc.baseCRC || 0) + (parseFloat(f.total_gravado_crc) || 0);
                acc.ivaCRC = (acc.ivaCRC || 0) + (parseFloat(f.total_impuesto_crc) || 0);
                return acc;
              }, { baseCRC: 0, ivaCRC: 0 });

              // Colones primero, después dólares
              const currencies = Object.keys(totalsByCurrency).filter(k => k !== 'baseCRC' && k !== 'ivaCRC')
                .sort((a, b) => (a === 'CRC' ? -1 : b === 'CRC' ? 1 : a.localeCompare(b)));

              return (
                <tfoot className="bg-gray-100 border-t-2 border-gray-300 sticky bottom-0 z-10">
                  {/* Igual que Facturas Aceptadas: una fila por moneda con Gravado/IVA/Total en su moneda.
                      Base CRC e IVA CRC son sumas globales (todo convertido) y van solo en la primera fila. */}
                  {currencies.map((moneda, idx) => {
                    const esPrimera = idx === 0;
                    const t = totalsByCurrency[moneda] || {};
                    return (
                      <tr key={moneda}>
                        <td className={`px-3 ${esPrimera ? 'pt-3' : 'pt-1'} pb-1`}></td>
                        {visibleColumns.map(colId => {
                          const isRight = ['gravado', 'iva', 'total', 'tipoCambio', 'baseCRC', 'ivaCRC'].includes(colId);
                          let content = '';
                          if (colId === 'proveedor') {
                            content = esPrimera ? <span className="font-bold text-gray-700">Totales:</span> : '';
                          } else if (colId === 'gravado') {
                            content = <span className="font-bold text-gray-900">{formatMoney(t.gravado || 0, moneda)}</span>;
                          } else if (colId === 'iva') {
                            content = <span className="font-bold text-gray-900">{formatMoney(t.iva || 0, moneda)}</span>;
                          } else if (colId === 'total') {
                            content = <span className="font-bold text-gray-900">{formatMoney(t.total || 0, moneda)}</span>;
                          } else if (colId === 'baseCRC') {
                            content = esPrimera ? <span className="font-bold text-gray-900">{formatMoney(totalsByCurrency.baseCRC, 'CRC')}</span> : '';
                          } else if (colId === 'ivaCRC') {
                            content = esPrimera ? <span className="font-bold text-gray-900">{formatMoney(totalsByCurrency.ivaCRC, 'CRC')}</span> : '';
                          }
                          return (
                            <td key={colId} className={`px-4 ${esPrimera ? 'pt-3' : 'pt-1'} pb-1 whitespace-nowrap ${isRight ? 'text-right' : ''}`}>
                              {content}
                            </td>
                          );
                        })}
                        <td className="px-2"></td>
                      </tr>
                    );
                  })}
                </tfoot>
              );
            })()}
          </table>
        </div>

        {/* Footer mínimo con conteo (sin paginación — todo el período en scroll interno) */}
        <div className="shrink-0 flex items-center justify-between px-4 py-2 border-t bg-gray-50 text-xs text-gray-600">
          <span>{facturas.length} FEC{facturas.length !== 1 ? 's' : ''} mostrada{facturas.length !== 1 ? 's' : ''}{pagination.total && pagination.total !== facturas.length ? ` de ${pagination.total}` : ''}</span>
          {facturas.length >= 5000 && (
            <span className="text-amber-600 font-medium">⚠ Límite 5000 alcanzado — filtre por período más corto</span>
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showModal && (
        <CrearFECModal
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            setShowModal(false);
            fetchFacturas();
          }}
        />
      )}

      {/* PDF Viewer Modal */}
      {viewPdfUrl && (
        <DraggableModal title="PDF Importado" onClose={() => { URL.revokeObjectURL(viewPdfUrl); setViewPdfUrl(null); }}>
          <iframe src={viewPdfUrl} className="w-full h-full" title="PDF FEC" />
        </DraggableModal>
      )}

      {/* Quién pagó la FEC de contado */}
      {pagadoPorModal && (
        <PagadoPorModal
          compraId={pagadoPorModal.factura.compra_id}
          titulo={`${pagadoPorModal.factura.proveedor_nombre} · FEC ${pagadoPorModal.factura.numero_consecutivo || ''}`}
          total={parseFloat(pagadoPorModal.factura.total_comprobante) || 0}
          moneda={pagadoPorModal.factura.moneda}
          inicial={pagadoPorModal}
          puedePasarACredito={!pagadoPorModal.factura.condicion_corregida}
          onPasarACredito={() => { const fac = pagadoPorModal.factura; setPagadoPorModal(null); setCorregirModal({ factura: fac, nueva: 'credito' }); }}
          onClose={() => setPagadoPorModal(null)}
          onGuardado={() => { setPagadoPorModal(null); fetchFacturas(); }}
        />
      )}

      {/* Modal: Corregir condición de venta (Contado ↔ Crédito) */}
      {corregirModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => !corrigiendo && setCorregirModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Corregir condición de venta</h3>
            <p className="text-sm text-gray-600 mb-4">
              Vas a cambiar la condición de esta FEC de
              <span className={`inline-block mx-1 px-2 py-0.5 rounded text-xs font-semibold ${corregirModal.factura.condicion_venta === '01' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                {corregirModal.factura.condicion_venta === '01' ? 'Contado' : 'Crédito'}
              </span>
              a
              <span className={`inline-block mx-1 px-2 py-0.5 rounded text-xs font-semibold ${corregirModal.nueva === 'contado' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                {corregirModal.nueva === 'contado' ? 'Contado' : 'Crédito'}
              </span>
            </p>

            {corregirModal.nueva === 'credito' ? (
              <>
                <p className="text-xs text-gray-500 mb-3">
                  Se va a <strong>crear automáticamente la CxP</strong> correspondiente con saldo pendiente. El monto será el subtotal sin IVA (el IVA autoliquidado se paga directo a Hacienda).
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plazo de crédito (días)</label>
                <input
                  type="number"
                  min="1"
                  max="365"
                  value={corregirPlazo}
                  onChange={(e) => setCorregirPlazo(parseInt(e.target.value) || 30)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-4"
                />
              </>
            ) : (
              <p className="text-xs text-gray-500 mb-3">
                Se eliminará la CxP asociada (siempre que no tenga pagos aplicados). Si ya pagaste algo, desaplicá los pagos primero.
              </p>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-amber-700">
                ⚠ Esta acción solo puede ejecutarse <strong>una vez</strong>. Después la FEC quedará marcada como corregida.
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setCorregirModal(null)}
                disabled={corrigiendo}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm"
              >Cancelar</button>
              <button
                onClick={async () => {
                  setCorrigiendo(true);
                  try {
                    await gastosApi.corregirCondicionFEC(corregirModal.factura.id, corregirModal.nueva, corregirPlazo);
                    toast.success(`Condición corregida a ${corregirModal.nueva}`);
                    setCorregirModal(null);
                    fetchFacturas();
                  } catch (e) {
                    toast.error(e.response?.data?.error || 'Error al corregir');
                  } finally {
                    setCorrigiendo(false);
                  }
                }}
                disabled={corrigiendo}
                className="px-4 py-2 border-2 border-amber-500 text-amber-600 rounded-lg hover:bg-amber-50 text-sm font-medium disabled:opacity-50"
              >
                {corrigiendo ? 'Corrigiendo...' : 'Confirmar corrección'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FEC Detail Modal */}
      {viewFecDetail && (
        <DraggableModal title={`FEC ${viewFecDetail.numero_consecutivo}`} onClose={() => setViewFecDetail(null)} maxWidth="max-w-6xl">
          <div className="flex flex-col lg:flex-row h-full">
          <div className="lg:w-[440px] lg:flex-shrink-0 overflow-auto p-6 space-y-4 lg:border-r border-gray-200">
            {/* Estado */}
            {viewFecDetail.estado_hacienda && (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${
                viewFecDetail.estado_hacienda === 'aceptado' ? 'bg-green-50 border-green-200 text-green-700' :
                viewFecDetail.estado_hacienda === 'rechazado' ? 'bg-red-50 border-red-200 text-red-700' :
                'bg-blue-50 border-blue-200 text-blue-700'
              }`}>
                {viewFecDetail.estado_hacienda === 'aceptado' ? <CheckCircle className="h-4 w-4" /> :
                 viewFecDetail.estado_hacienda === 'rechazado' ? <XCircle className="h-4 w-4" /> :
                 <Clock className="h-4 w-4" />}
                Estado Hacienda: {viewFecDetail.estado_hacienda}
                {viewFecDetail.respuesta_hacienda && viewFecDetail.estado_hacienda === 'rechazado' && (
                  <span className="text-xs font-normal ml-2">— {viewFecDetail.respuesta_hacienda.substring(0, 150)}</span>
                )}
              </div>
            )}

            {/* Proveedor */}
            <div className="border rounded-lg p-4">
              <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Proveedor</h4>
              <p className="font-semibold text-gray-900">{viewFecDetail.proveedor_nombre}</p>
              <p className="text-sm text-gray-500">{viewFecDetail.proveedor_tipo_id === '05' ? 'Extranjero' : 'Cédula'}: {viewFecDetail.proveedor_identificacion}</p>
              {viewFecDetail.proveedor_email && <p className="text-sm text-gray-500">{viewFecDetail.proveedor_email}</p>}
            </div>

            {/* Info General */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Consecutivo</p>
                <p className="font-mono text-sm font-medium">{viewFecDetail.numero_consecutivo}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">No. Factura Prov.</p>
                <p className="text-sm font-medium">{viewFecDetail.referencia_numero || '-'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Fecha</p>
                <p className="text-sm font-medium">{viewFecDetail.fecha_emision ? dayjs(viewFecDetail.fecha_emision).format('DD/MM/YYYY') : '-'}</p>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500">Condición</p>
                <p className="text-sm font-medium">{viewFecDetail.condicion_venta === '01' ? 'Contado' : viewFecDetail.condicion_venta === '02' ? 'Crédito' : viewFecDetail.condicion_venta}</p>
              </div>
            </div>

            {/* Líneas */}
            {viewFecDetail.lineas?.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">#</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">CABYS</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Detalle</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Cant</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Precio</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">IVA</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {viewFecDetail.lineas.map((l, i) => (
                      <tr key={i} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-400">{l.numero_linea || i + 1}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-500">{l.codigo_cabys}</td>
                        <td className="px-3 py-2">{l.detalle}</td>
                        <td className="px-3 py-2 text-right">{l.cantidad}</td>
                        <td className="px-3 py-2 text-right font-mono">{parseFloat(l.precio_unitario || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</td>
                        <td className="px-3 py-2 text-right">{l.impuesto_tarifa || 0}%</td>
                        <td className="px-3 py-2 text-right font-mono font-medium">{parseFloat(l.monto_total_linea || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totales */}
            <div className="bg-gray-50 rounded-lg p-4 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div className="flex justify-between"><span>Moneda:</span><span className="font-medium">{viewFecDetail.moneda}</span></div>
                {viewFecDetail.moneda === 'USD' && <div className="flex justify-between"><span>T/C:</span><span className="font-medium">₡{parseFloat(viewFecDetail.tipo_cambio || 0).toFixed(2)}</span></div>}
                <div className="flex justify-between"><span>Gravado:</span><span className="font-mono">{parseFloat(viewFecDetail.total_gravado || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span>Exento:</span><span className="font-mono">{parseFloat(viewFecDetail.total_exento || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between"><span>IVA:</span><span className="font-mono">{parseFloat(viewFecDetail.total_impuesto || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</span></div>
                <div className="flex justify-between font-bold text-base border-t pt-2"><span>Total:</span><span className="font-mono">{parseFloat(viewFecDetail.total_comprobante || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}</span></div>
              </div>
            </div>

          </div>{/* fin columna izquierda (resumen) */}

          {/* Columna derecha: PDF original de la factura del proveedor */}
          <div className="flex-1 flex flex-col bg-gray-100 min-h-[320px] lg:min-h-0">
            {viewFecDetail.pdf_storage_path ? (
              <>
                <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200 flex-shrink-0">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider flex items-center gap-2">
                    <FileText className="h-4 w-4" /> Factura del proveedor (PDF)
                  </span>
                  {fecPdfUrl && (
                    <button onClick={() => window.open(fecPdfUrl, '_blank')}
                      className="text-xs text-blue-600 hover:text-blue-800 hover:underline">
                      Abrir en pestaña ↗
                    </button>
                  )}
                </div>
                {fecPdfLoading ? (
                  <div className="flex-1 flex items-center justify-center text-gray-400">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" /> Cargando PDF…
                  </div>
                ) : fecPdfUrl ? (
                  <iframe src={fecPdfUrl} className="flex-1 w-full" title="PDF FEC" />
                ) : (
                  <div className="flex-1 flex items-center justify-center text-gray-400 text-sm">No se pudo cargar el PDF</div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center p-6">
                <div className="flex items-center gap-2 px-4 py-3 bg-amber-50 border-2 border-amber-300 rounded-lg text-sm text-amber-700 font-medium max-w-sm">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0" />
                  Esta FEC fue ingresada manualmente — no tiene PDF asociado de la factura del proveedor.
                </div>
              </div>
            )}
          </div>{/* fin columna derecha (PDF) */}
          </div>
        </DraggableModal>
      )}

      {/* Edit Rejected FEC Modal */}
      {editFecData && (
        <EditarFECModal
          fecData={editFecData}
          onClose={() => setEditFecData(null)}
          onSuccess={() => {
            setEditFecData(null);
            fetchFacturas();
          }}
        />
      )}

      {/* Import PDF Modal */}
      {showImportarModal && (
        <ImportarFECModal
          onClose={() => setShowImportarModal(false)}
          onSuccess={() => {
            setShowImportarModal(false);
            fetchFacturas();
          }}
        />
      )}

      {/* Modal Eliminar FEC */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 p-4 border-b bg-red-50 rounded-t-xl">
              <div className="p-2 bg-red-100 rounded-lg">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Eliminar FEC</h2>
                <p className="text-sm text-gray-600">Esta acción no se puede deshacer</p>
              </div>
            </div>
            <div className="p-4">
              <p className="text-gray-700">
                ¿Está seguro que desea eliminar <strong>{selectedIds.size} FEC{selectedIds.size > 1 ? 's' : ''}</strong> seleccionada{selectedIds.size > 1 ? 's' : ''}?
              </p>
              <p className="text-sm text-gray-500 mt-2">Solo se pueden eliminar las FEC que no han sido aceptadas por Hacienda.</p>
            </div>
            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setShowDeleteModal(false)} disabled={deleting}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 disabled:opacity-50">
                Cancelar
              </button>
              <button
                onClick={async () => { setShowDeleteModal(false); await handleDeleteSelected(); }}
                disabled={deleting}
                className="px-4 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                {deleting ? 'Eliminando...' : `Eliminar (${selectedIds.size})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {catFec && catFec.length > 0 && (
        <CategorizarFECModal
          fecs={catFec}
          onClose={() => setCatFec(null)}
          onSaved={() => { setCatFec(null); setSelectedIds(new Set()); fetchFacturas(); }}
        />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════
// CategorizarFECModal — categoriza el IVA acreditable de las líneas de una FEC
// (aplica una categoría a todas las líneas del companion en facturas_compra)
// ══════════════════════════════════════════════════════════════════════
function CategorizarFECModal({ fecs, onClose, onSaved }) {
  const [clase, setClase] = useState('S');           // B=Bien, S=Servicio, K=Capital
  const [movimiento, setMovimiento] = useState('01'); // 01=Con derecho, 02=Sin derecho (no relacionadas)
  const [identificacion, setIdentificacion] = useState('E'); // E=Identificación específica (100%), P=No identificable (prorrata)
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [saving, onClose]);

  const guardar = async () => {
    setSaving(true);
    const payload = {
      tipo_movimiento: movimiento,
      clase,
      identificacion: movimiento === '01' ? identificacion : 'P',
      tarifa_iva_categoria: '13'
    };
    let ok = 0, fail = 0;
    for (const fec of fecs) {
      try {
        await gastosApi.categorizarFactura(fec.compra_id, payload);
        ok++;
      } catch { fail++; }
    }
    setSaving(false);
    if (ok > 0) toast.success(fecs.length === 1 ? 'Líneas categorizadas' : `${ok} FEC categorizada${ok === 1 ? '' : 's'}`);
    if (fail > 0) toast.error(`${fail} FEC no se pudo categorizar`);
    if (ok > 0) onSaved();
  };

  const nFec = fecs.length;
  const nLineas = fecs.reduce((s, f) => s + (parseInt(f.lineas_sin_categorizar) || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !saving && onClose()}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-purple-600 to-purple-700 rounded-t-xl">
          <h3 className="font-semibold text-white flex items-center gap-2 text-lg">
            <FolderTree className="h-5 w-5" /> Categorizar IVA — {nFec === 1 ? fecs[0].numero_consecutivo : `${nFec} FEC`}
          </h3>
          <button onClick={() => !saving && onClose()} className="text-white/70 hover:text-white"><X className="h-5 w-5" /></button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-gray-600">
            Se aplicará la misma categoría a las <strong>{nLineas} línea{nLineas === 1 ? '' : 's'}</strong> sin categorizar de <strong>{nFec} FEC</strong> (autoliquidación).
          </p>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Clase</label>
            <select value={clase} onChange={e => setClase(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
              <option value="B">Bien</option>
              <option value="S">Servicio</option>
              <option value="K">Bien de capital</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Movimiento (derecho a crédito)</label>
            <select value={movimiento} onChange={e => setMovimiento(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
              <option value="01">Con derecho a crédito (operaciones gravadas)</option>
              <option value="02">Sin derecho a crédito</option>
            </select>
          </div>

          {movimiento === '01' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Identificación</label>
              <select value={identificacion} onChange={e => setIdentificacion(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-200">
                <option value="E">Identificación específica (100% acreditable)</option>
                <option value="P">No identificable (aplica prorrata)</option>
              </select>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button onClick={() => !saving && onClose()} className="px-5 py-2 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium">Cancelar</button>
          <button onClick={guardar} disabled={saving}
            className="px-6 py-2 border-2 border-purple-600 bg-purple-600 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-50 hover:bg-purple-700">
            <Check className="h-4 w-4" /> {saving ? 'Guardando...' : 'Categorizar'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Modal para crear FEC
function CrearFECModal({ onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: Proveedor, 2: Lineas, 3: Confirmar
  const [proveedores, setProveedores] = useState([]);
  const [searchProveedor, setSearchProveedor] = useState('');
  const [loadingProveedores, setLoadingProveedores] = useState(false);
  const [consultandoHacienda, setConsultandoHacienda] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Proveedor data
  const [proveedorMode, setProveedorMode] = useState('existing'); // 'existing' | 'new'
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [newProveedor, setNewProveedor] = useState({
    tipoIdentificacion: '02',
    identificacion: '',
    nombre: '',
    nombreComercial: '',
    email: '',
    pais: 'CRI',
    direccion: ''
  });

  // Factura data
  const [fechaEmision, setFechaEmision] = useState(dayjs().format('YYYY-MM-DD'));
  const [numeroFacturaProveedor, setNumeroFacturaProveedor] = useState('');
  const [condicionVenta, setCondicionVenta] = useState('01');
  const [plazoCredito, setPlazoCredito] = useState('30');
  const [medioPago, setMedioPago] = useState('02');
  const [moneda, setMoneda] = useState('USD');
  const [tipoCambio, setTipoCambio] = useState('1');
  const [fecSoloCredito, setFecSoloCredito] = useState(false); // IVA transfronterizo: banco ya retuvo

  // Lineas ya agregadas
  const [lineas, setLineas] = useState([]);

  // Línea actual siendo editada (formato Libro)
  const [lineaActual, setLineaActual] = useState({
    codigoCabys: '',
    descripcionCabys: '',
    detalle: '',
    cantidad: '1',
    unidadMedida: 'Unid',
    precioUnitario: '',
    impuestoTarifa: '13',
    esServicio: true,
    // Categorización
    tipo_movimiento: 'C',
    clase: 'G',
    identificacion: 'E'
  });

  // CABYS Explorer
  const [showCabysExplorer, setShowCabysExplorer] = useState(false);

  // Categorías para IVA
  const [categorias, setCategorias] = useState(null);

  // Cargar categorías al montar
  useEffect(() => {
    const loadCategorias = async () => {
      try {
        const response = await gastosApi.getCategorias();
        setCategorias(response.data.data);
      } catch (error) {
        console.error('Error loading categorias:', error);
      }
    };
    loadCategorias();
  }, []);

  // Cargar tipo de cambio cuando moneda es USD o cambia la fecha
  useEffect(() => {
    const fetchTipoCambio = async () => {
      if (moneda === 'USD') {
        try {
          // Use the API URL from environment
          const apiUrl = import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/internal/exchange-rate/${fechaEmision}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'X-Tenant-Slug': localStorage.getItem('tenantSlug')
            }
          });
          const data = await response.json();
          if (data?.success && data?.venta) {
            setTipoCambio(data.venta.toString());
          }
        } catch (error) {
          console.error('Error fetching exchange rate:', error);
        }
      } else {
        setTipoCambio('1');
      }
    };
    fetchTipoCambio();
  }, [moneda, fechaEmision]);

  // Search proveedores
  useEffect(() => {
    const fetchProveedores = async () => {
      if (!searchProveedor || searchProveedor.length < 2) {
        setProveedores([]);
        return;
      }
      try {
        setLoadingProveedores(true);
        const response = await gastosApi.searchProveedores(searchProveedor);
        setProveedores(response.data.data || []);
      } catch (error) {
        console.error('Error searching proveedores:', error);
      } finally {
        setLoadingProveedores(false);
      }
    };

    const debounce = setTimeout(fetchProveedores, 300);
    return () => clearTimeout(debounce);
  }, [searchProveedor]);

  // Consultar Hacienda
  const handleConsultarHacienda = async () => {
    if (!newProveedor.identificacion || newProveedor.identificacion.length < 9) {
      toast.error('Ingrese una identificacion valida');
      return;
    }

    // Solo para tipos nacionales (01, 02, 03, 04)
    if (!['01', '02', '03', '04'].includes(newProveedor.tipoIdentificacion)) {
      toast('Consulta a Hacienda solo disponible para identificaciones nacionales');
      return;
    }

    try {
      setConsultandoHacienda(true);
      const response = await gastosApi.consultarProveedorHacienda(newProveedor.identificacion);

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setNewProveedor(prev => ({
          ...prev,
          nombre: data.nombre || prev.nombre,
          tipoIdentificacion: data.tipoIdentificacion || prev.tipoIdentificacion
        }));
        toast.success('Datos obtenidos de Hacienda');
      } else {
        toast.error(response.data.error || 'No se encontro el contribuyente');
      }
    } catch (error) {
      toast.error('Error al consultar Hacienda');
    } finally {
      setConsultandoHacienda(false);
    }
  };

  // Reset línea actual
  const resetLineaActual = () => {
    setLineaActual({
      codigoCabys: '',
      descripcionCabys: '',
      detalle: '',
      cantidad: '1',
      unidadMedida: 'Unid',
      precioUnitario: '',
      impuestoTarifa: '13',
      esServicio: true,
      tipo_movimiento: 'C',
      clase: 'G',
      identificacion: 'E'
    });
  };

  // Incluir línea (formato Libro)
  const incluirLinea = () => {
    if (!lineaActual.codigoCabys || !lineaActual.detalle || !lineaActual.precioUnitario) {
      toast.error('Complete los campos requeridos: CABYS, Detalle y Precio');
      return;
    }
    setLineas([...lineas, { ...lineaActual }]);
    resetLineaActual();
    toast.success('Línea agregada');
  };

  // Remove line
  const removeLinea = (index) => {
    setLineas(lineas.filter((_, i) => i !== index));
  };

  // Update línea actual
  const updateLineaActual = (field, value) => {
    setLineaActual(prev => ({ ...prev, [field]: value }));
  };

  // Handle CABYS selection (formato Libro - siempre para lineaActual)
  const handleSelectCabys = (cabys) => {
    // Parsear el impuesto de CABYS (puede ser string o number)
    const impuestoNum = parseFloat(cabys.impuesto);
    // Solo usar el impuesto de CABYS si es un número válido > 0
    // De lo contrario, mantener el default 13%
    const usarImpuestoCabys = !isNaN(impuestoNum) && impuestoNum > 0;

    setLineaActual(prev => ({
      ...prev,
      codigoCabys: cabys.codigo,
      descripcionCabys: cabys.descripcion,
      detalle: cabys.descripcion,
      // Mantener 13% si CABYS no tiene impuesto válido > 0
      impuestoTarifa: usarImpuestoCabys ? impuestoNum.toString() : '13'
    }));
    setShowCabysExplorer(false);
  };

  // Open CABYS explorer
  const openCabysExplorer = () => {
    setShowCabysExplorer(true);
  };

  // Calculate totals (solo de líneas ya agregadas)
  const calcularTotales = () => {
    let totalGravado = 0;
    let totalExento = 0;
    let totalImpuesto = 0;

    lineas.forEach(linea => {
      const cantidad = parseFloat(linea.cantidad) || 0;
      const precio = parseFloat(linea.precioUnitario) || 0;
      const subtotal = cantidad * precio;
      const tarifa = parseFloat(linea.impuestoTarifa) || 0;
      const impuesto = subtotal * (tarifa / 100);

      if (tarifa > 0) {
        totalGravado += subtotal;
        totalImpuesto += impuesto;
      } else {
        totalExento += subtotal;
      }
    });

    return {
      totalGravado,
      totalExento,
      totalImpuesto,
      totalComprobante: totalGravado + totalExento + totalImpuesto
    };
  };

  // Calcular subtotal de línea actual
  const calcularSubtotalLineaActual = () => {
    const cantidad = parseFloat(lineaActual.cantidad) || 0;
    const precio = parseFloat(lineaActual.precioUnitario) || 0;
    return cantidad * precio;
  };

  const totales = calcularTotales();

  // Submit
  const handleSubmit = async () => {
    // Validate
    const proveedor = proveedorMode === 'existing' ? selectedProveedor : newProveedor;

    if (!proveedor || !proveedor.identificacion || !proveedor.nombre) {
      toast.error('Complete los datos del proveedor');
      return;
    }

    // Validar dirección para proveedores extranjeros (direccion para nuevo, otrasSenas para existente)
    if (proveedor.tipoIdentificacion === '05') {
      const direccionExtranjero = proveedor.direccion || proveedor.otrasSenas || '';
      if (direccionExtranjero.length < 5) {
        toast.error('Para proveedores extranjeros se requiere una direccion de al menos 5 caracteres');
        return;
      }
    }

    if (lineas.length === 0) {
      toast.error('Debe agregar al menos una línea de detalle');
      return;
    }

    try {
      setSubmitting(true);

      const data = {
        proveedorId: proveedorMode === 'existing' ? selectedProveedor.id : null,
        proveedor: proveedorMode === 'new' ? newProveedor : null,
        lineas: lineas.map(l => {
          const subtotal = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precioUnitario) || 0);
          const tarifaIva = parseFloat(l.impuestoTarifa) || 0;
          const ivaTotal = subtotal * (tarifaIva / 100);
          const esIdentificado = l.identificacion === 'E';

          return {
            codigoCabys: l.codigoCabys,
            detalle: l.detalle,
            cantidad: l.cantidad,
            unidadMedida: l.unidadMedida,
            precioUnitario: l.precioUnitario,
            impuestoTarifa: l.impuestoTarifa,
            esServicio: l.esServicio,
            // Categorización
            tipo_movimiento: l.tipo_movimiento,
            clase: l.clase,
            identificacion: l.identificacion,
            // IVA calculado según identificación
            tarifa_iva_categoria: tarifaIva.toString(),
            iva_acreditar: esIdentificado ? ivaTotal : 0,
            iva_gasto: esIdentificado ? 0 : ivaTotal
          };
        }),
        fechaEmision,
        numeroFacturaProveedor,
        condicionVenta,
        plazoCredito: condicionVenta === '02' ? plazoCredito : null,
        medioPago,
        moneda,
        tipoCambio,
        fecSoloCredito
      };

      const response = await gastosApi.createFEC(data);

      if (response.data.success) {
        toast.success(`FEC enviada a Hacienda. Consecutivo: ${response.data.consecutivo}`);
        onSuccess();
      } else {
        toast.error(response.data.error || 'Error al crear FEC');
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear FEC');
    } finally {
      setSubmitting(false);
    }
  };

  const formatMoneyModal = (amount) => {
    const num = parseFloat(amount || 0);
    if (moneda === 'USD') {
      return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <FileText className="text-primary-600" size={24} />
            <div>
              <h2 className="text-xl font-bold text-gray-900">Nueva Factura Electronica de Compra</h2>
              <p className="text-sm text-gray-500">Paso {step} de 3</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: Proveedor */}
          {step === 1 && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg text-gray-900">Datos del Proveedor</h3>

              {/* Mode selector */}
              <div className="flex gap-4">
                <button
                  onClick={() => setProveedorMode('existing')}
                  className={`flex-1 p-4 border-2 rounded-lg text-center transition-all ${
                    proveedorMode === 'existing'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Building2 size={24} className="mx-auto mb-2" />
                  <div className="font-medium">Proveedor Existente</div>
                  <div className="text-xs text-gray-500">Seleccionar de mis proveedores</div>
                </button>
                <button
                  onClick={() => setProveedorMode('new')}
                  className={`flex-1 p-4 border-2 rounded-lg text-center transition-all ${
                    proveedorMode === 'new'
                      ? 'border-primary-500 bg-primary-50 text-primary-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <UserPlus size={24} className="mx-auto mb-2" />
                  <div className="font-medium">Nuevo Proveedor</div>
                  <div className="text-xs text-gray-500">Ingresar datos manualmente</div>
                </button>
              </div>

              {/* Existing proveedor */}
              {proveedorMode === 'existing' && (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                    <input
                      type="text"
                      placeholder="Buscar proveedor por nombre o identificacion..."
                      value={searchProveedor}
                      onChange={(e) => setSearchProveedor(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>

                  {loadingProveedores && (
                    <div className="text-center py-4 text-gray-500">Buscando...</div>
                  )}

                  {proveedores.length > 0 && (
                    <div className="border rounded-lg divide-y max-h-60 overflow-y-auto">
                      {proveedores.map(prov => (
                        <button
                          key={prov.id}
                          onClick={() => {
                            setSelectedProveedor(prov);
                            setSearchProveedor('');
                            setProveedores([]);
                          }}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center justify-between"
                        >
                          <div>
                            <div className="font-medium">{prov.nombre}</div>
                            <div className="text-sm text-gray-500">{prov.identificacion}</div>
                          </div>
                          <CheckCircle size={18} className="text-gray-300" />
                        </button>
                      ))}
                    </div>
                  )}

                  {selectedProveedor && (
                    <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium text-green-800">{selectedProveedor.nombre}</div>
                          <div className="text-sm text-green-600">{selectedProveedor.identificacion}</div>
                          {selectedProveedor.email && (
                            <div className="text-sm text-green-600">{selectedProveedor.email}</div>
                          )}
                        </div>
                        <button
                          onClick={() => setSelectedProveedor(null)}
                          className="text-green-600 hover:text-green-800"
                        >
                          <X size={18} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* New proveedor */}
              {proveedorMode === 'new' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tipo Identificacion
                      </label>
                      <select
                        value={newProveedor.tipoIdentificacion}
                        onChange={(e) => setNewProveedor({ ...newProveedor, tipoIdentificacion: e.target.value })}
                        className="w-full border rounded-lg px-3 py-2"
                      >
                        {tipoIdOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Identificacion
                      </label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newProveedor.identificacion}
                          onChange={(e) => setNewProveedor({ ...newProveedor, identificacion: e.target.value })}
                          placeholder={newProveedor.tipoIdentificacion === '05' ? 'ID extranjero' : '0-0000-0000'}
                          className="flex-1 border rounded-lg px-3 py-2"
                        />
                        {['01', '02', '03', '04'].includes(newProveedor.tipoIdentificacion) && (
                          <button
                            onClick={handleConsultarHacienda}
                            disabled={consultandoHacienda}
                            className="px-3 py-2 border-2 border-slate-500 text-slate-600 rounded-lg hover:bg-slate-50 disabled:opacity-50 text-sm whitespace-nowrap"
                          >
                            {consultandoHacienda ? <Loader2 size={16} className="animate-spin" /> : 'Consultar'}
                          </button>
                        )}
                      </div>
                      {['01', '02', '03', '04'].includes(newProveedor.tipoIdentificacion) && (
                        <p className="text-xs text-gray-500 mt-1">
                          Consulta a Hacienda para obtener datos automaticamente
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre / Razon Social *
                    </label>
                    <input
                      type="text"
                      value={newProveedor.nombre}
                      onChange={(e) => setNewProveedor({ ...newProveedor, nombre: e.target.value })}
                      placeholder="Nombre completo del proveedor"
                      className="w-full border rounded-lg px-3 py-2"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Nombre Comercial
                      </label>
                      <input
                        type="text"
                        value={newProveedor.nombreComercial}
                        onChange={(e) => setNewProveedor({ ...newProveedor, nombreComercial: e.target.value })}
                        placeholder="Opcional"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={newProveedor.email}
                        onChange={(e) => setNewProveedor({ ...newProveedor, email: e.target.value })}
                        placeholder="proveedor@email.com"
                        className="w-full border rounded-lg px-3 py-2"
                      />
                    </div>
                  </div>

                  {newProveedor.tipoIdentificacion === '05' && (
                    <div className="space-y-3">
                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <Globe className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
                          <div className="text-sm text-yellow-800">
                            <strong>Proveedor Extranjero:</strong> Para servicios/intangibles del exterior,
                            debera autoliquidar el IVA correspondiente.
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            País *
                          </label>
                          <select
                            value={newProveedor.pais}
                            onChange={(e) => setNewProveedor({ ...newProveedor, pais: e.target.value })}
                            className="w-full border rounded-lg px-3 py-2"
                          >
                            <option value="">Seleccione país</option>
                            <option value="USA">Estados Unidos</option>
                            <option value="ESP">España</option>
                            <option value="MEX">México</option>
                            <option value="COL">Colombia</option>
                            <option value="ARG">Argentina</option>
                            <option value="CHL">Chile</option>
                            <option value="PER">Perú</option>
                            <option value="BRA">Brasil</option>
                            <option value="PAN">Panamá</option>
                            <option value="GTM">Guatemala</option>
                            <option value="SLV">El Salvador</option>
                            <option value="HND">Honduras</option>
                            <option value="NIC">Nicaragua</option>
                            <option value="CAN">Canadá</option>
                            <option value="GBR">Reino Unido</option>
                            <option value="DEU">Alemania</option>
                            <option value="FRA">Francia</option>
                            <option value="ITA">Italia</option>
                            <option value="CHN">China</option>
                            <option value="JPN">Japón</option>
                            <option value="IND">India</option>
                            <option value="AUS">Australia</option>
                            <option value="OTR">Otro</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Dirección *
                          </label>
                          <input
                            type="text"
                            value={newProveedor.direccion}
                            onChange={(e) => setNewProveedor({ ...newProveedor, direccion: e.target.value })}
                            placeholder="Dirección completa"
                            className="w-full border rounded-lg px-3 py-2"
                            minLength={5}
                            required
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">
                        Requerido por Hacienda para proveedores extranjeros
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Lineas - Formato Libro */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Datos básicos de la factura */}
              <div className="p-4 bg-gray-50 rounded-lg border">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha Emisión *
                    </label>
                    <input
                      type="date"
                      value={fechaEmision}
                      onChange={(e) => setFechaEmision(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nº Fact. Proveedor
                    </label>
                    <input
                      type="text"
                      value={numeroFacturaProveedor}
                      onChange={(e) => setNumeroFacturaProveedor(e.target.value)}
                      placeholder="Ej: INV-001"
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                    <select
                      value={moneda}
                      onChange={(e) => setMoneda(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      <option value="USD">USD - Dólares</option>
                      <option value="CRC">CRC - Colones</option>
                    </select>
                  </div>
                  {moneda === 'USD' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">T/C (₡)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={tipoCambio}
                        onChange={(e) => setTipoCambio(e.target.value)}
                        className="w-full border rounded-lg px-3 py-2 text-sm bg-amber-50"
                        placeholder="0.00"
                      />
                      <p className="text-xs text-amber-600 mt-0.5">BCCR del día</p>
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condición</label>
                    <select
                      value={condicionVenta}
                      onChange={(e) => setCondicionVenta(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      {condicionVentaOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Medio Pago</label>
                    <select
                      value={medioPago}
                      onChange={(e) => setMedioPago(e.target.value)}
                      className="w-full border rounded-lg px-3 py-2 text-sm"
                    >
                      {medioPagoOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                {condicionVenta === '02' && (
                  <div className="mt-3 flex items-center gap-2">
                    <label className="text-sm text-gray-600">Plazo de crédito:</label>
                    <input
                      type="number"
                      value={plazoCredito}
                      onChange={(e) => setPlazoCredito(e.target.value)}
                      className="w-20 border rounded px-2 py-1 text-sm"
                      min="1"
                    />
                    <span className="text-sm text-gray-500">días</span>
                  </div>
                )}
              </div>

              {/* IVA Transfronterizo - Solo Crédito */}
              <div className={`p-4 rounded-lg border-2 transition-colors ${fecSoloCredito ? 'bg-blue-50 border-blue-300' : 'bg-gray-50 border-gray-200'}`}>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fecSoloCredito}
                    onChange={(e) => setFecSoloCredito(e.target.checked)}
                    className="w-5 h-5 mt-0.5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                  />
                  <div>
                    <div className="font-medium text-gray-900">IVA Transfronterizo (Solo Crédito)</div>
                    <p className="text-sm text-gray-600 mt-1">
                      Marcar cuando el banco ya retuvo el IVA en el pago (ej: servicios digitales internacionales como Google, AWS, etc.).
                      En este caso, solo se genera <strong className="text-emerald-600">Crédito Fiscal</strong> (no Débito),
                      porque la autoliquidación ya fue retenida por el banco.
                    </p>
                    {fecSoloCredito && (
                      <div className="mt-2 p-2 bg-blue-100 rounded text-sm text-blue-800">
                        <strong>Nota:</strong> Esta FEC generará únicamente Crédito Fiscal. El Débito ya fue pagado al banco como retención.
                      </div>
                    )}
                  </div>
                </label>
              </div>

              {/* Layout Libro: Izquierda Preview / Derecha Formulario */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* IZQUIERDA: Preview de líneas agregadas */}
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-white min-h-[400px]">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Eye size={18} className="text-gray-500" />
                      Vista Previa FEC
                    </h3>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded">
                      {lineas.length} línea{lineas.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {lineas.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-gray-400">
                      <FileText size={48} className="mb-3 opacity-50" />
                      <p className="text-sm">No hay líneas agregadas</p>
                      <p className="text-xs mt-1">Use el formulario de la derecha para agregar líneas</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {/* Tabla de líneas */}
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">#</th>
                              <th className="px-2 py-1.5 text-left text-xs font-medium text-gray-500">Detalle</th>
                              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500">Cant.</th>
                              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500">Precio</th>
                              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500">IVA</th>
                              <th className="px-2 py-1.5 text-right text-xs font-medium text-gray-500">Total</th>
                              <th className="px-2 py-1.5 w-8"></th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {lineas.map((linea, index) => {
                              const subtotal = (parseFloat(linea.cantidad) || 0) * (parseFloat(linea.precioUnitario) || 0);
                              const iva = subtotal * (parseFloat(linea.impuestoTarifa) || 0) / 100;
                              const total = subtotal + iva;
                              return (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="px-2 py-1.5 text-gray-500">{index + 1}</td>
                                  <td className="px-2 py-1.5">
                                    <div className="truncate max-w-[150px]" title={linea.detalle}>
                                      {linea.detalle}
                                    </div>
                                    <div className="text-xs text-gray-400 font-mono">{linea.codigoCabys}</div>
                                  </td>
                                  <td className="px-2 py-1.5 text-right">{linea.cantidad}</td>
                                  <td className="px-2 py-1.5 text-right">
                                    {moneda === 'USD' ? '$' : '₡'}{parseFloat(linea.precioUnitario || 0).toLocaleString(undefined, {minimumFractionDigits: 2})}
                                  </td>
                                  <td className="px-2 py-1.5 text-right text-gray-500">{linea.impuestoTarifa}%</td>
                                  <td className="px-2 py-1.5 text-right font-medium">
                                    {moneda === 'USD' ? '$' : '₡'}{total.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <button
                                      onClick={() => removeLinea(index)}
                                      className="p-0.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                    >
                                      <X size={14} />
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Totales Preview */}
                      <div className="border-t-2 border-gray-300 pt-2 mt-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Gravado:</span>
                          <span>{formatMoneyModal(totales.totalGravado)}</span>
                        </div>
                        {totales.totalExento > 0 && (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">Exento:</span>
                            <span>{formatMoneyModal(totales.totalExento)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">IVA:</span>
                          <span>{formatMoneyModal(totales.totalImpuesto)}</span>
                        </div>
                        <div className="flex justify-between font-bold text-lg mt-1 pt-1 border-t">
                          <span>TOTAL:</span>
                          <span>{formatMoneyModal(totales.totalComprobante)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* DERECHA: Formulario para agregar línea */}
                <div className="border-2 border-primary-200 rounded-lg p-4 bg-primary-50/30">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Plus size={18} className="text-primary-600" />
                    Agregar Línea
                  </h3>

                  <div className="space-y-3">
                    {/* CABYS */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">CABYS *</label>
                      <div className="flex gap-2">
                        <div className="flex-1 flex items-center gap-2 px-3 py-1.5 border rounded bg-white text-sm min-h-[36px]">
                          {lineaActual.codigoCabys ? (
                            <>
                              <span className="font-mono text-primary-600">{lineaActual.codigoCabys}</span>
                              <span className="text-gray-500 truncate text-xs">{lineaActual.descripcionCabys}</span>
                            </>
                          ) : (
                            <span className="text-gray-400">Seleccione código CABYS</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={openCabysExplorer}
                          className="flex items-center gap-1 px-3 py-1.5 bg-slate-700 text-white rounded hover:bg-slate-600 text-sm"
                        >
                          <FolderTree size={14} />
                          Buscar
                        </button>
                      </div>
                    </div>

                    {/* Detalle */}
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Detalle *</label>
                      <input
                        type="text"
                        value={lineaActual.detalle}
                        onChange={(e) => updateLineaActual('detalle', e.target.value)}
                        placeholder="Descripción del producto/servicio"
                        className="w-full border rounded px-3 py-1.5 text-sm"
                      />
                    </div>

                    {/* Cantidad, Unidad, Precio, IVA */}
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Cantidad</label>
                        <input
                          type="number"
                          step="0.001"
                          value={lineaActual.cantidad}
                          onChange={(e) => updateLineaActual('cantidad', e.target.value)}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Unidad</label>
                        <input
                          type="text"
                          value={lineaActual.unidadMedida}
                          onChange={(e) => updateLineaActual('unidadMedida', e.target.value)}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Precio *</label>
                        <input
                          type="number"
                          step="0.01"
                          value={lineaActual.precioUnitario}
                          onChange={(e) => updateLineaActual('precioUnitario', e.target.value)}
                          placeholder="0.00"
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">IVA %</label>
                        <select
                          value={lineaActual.impuestoTarifa}
                          onChange={(e) => updateLineaActual('impuestoTarifa', e.target.value)}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                        >
                          <option value="0">0%</option>
                          <option value="1">1%</option>
                          <option value="2">2%</option>
                          <option value="4">4%</option>
                          <option value="8">8%</option>
                          <option value="13">13%</option>
                        </select>
                      </div>
                    </div>

                    {/* Subtotal de línea actual */}
                    <div className="bg-gray-100 rounded p-2 flex justify-between items-center">
                      <span className="text-sm text-gray-600">Subtotal línea:</span>
                      <span className="font-medium">
                        {moneda === 'USD' ? '$' : '₡'}{calcularSubtotalLineaActual().toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                      </span>
                    </div>

                    {/* Categorización IVA */}
                    {categorias && (() => {
                      const subtotal = calcularSubtotalLineaActual();
                      const tarifaIva = parseFloat(lineaActual.impuestoTarifa) || 0;
                      const ivaTotal = subtotal * (tarifaIva / 100);
                      const esIdentificado = lineaActual.identificacion === 'E';
                      const ivaAcreditar = esIdentificado ? ivaTotal : 0;
                      const ivaGasto = esIdentificado ? 0 : ivaTotal;

                      return (
                        <div className="pt-3 border-t border-dashed border-gray-300">
                          <div className="text-xs font-medium text-purple-600 mb-2">Categorización IVA</div>
                          <div className="grid grid-cols-2 gap-2 mb-2">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Clase</label>
                              <select
                                value={lineaActual.clase}
                                onChange={(e) => updateLineaActual('clase', e.target.value)}
                                className="w-full border rounded px-2 py-1.5 text-sm"
                              >
                                {categorias.clases.map(c => (
                                  <option key={c.codigo} value={c.codigo}>{c.nombre}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">Identificación</label>
                              <select
                                value={lineaActual.identificacion}
                                onChange={(e) => updateLineaActual('identificacion', e.target.value)}
                                className="w-full border rounded px-2 py-1.5 text-sm"
                              >
                                {categorias.identificaciones.map(i => (
                                  <option key={i.codigo} value={i.codigo}>{i.nombre}</option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {/* Resumen IVA */}
                          {esIdentificado ? (
                            <div className="bg-green-50 border border-green-200 rounded p-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-gray-600">IVA Acreditar:</span>
                                <span className="font-medium text-green-700">
                                  {moneda === 'USD' ? '$' : '₡'}{ivaAcreditar.toLocaleString(undefined, {minimumFractionDigits: 2})}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                              <span className="text-amber-700">Según Proporcionalidad</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* Botón Incluir Línea */}
                    <button
                      onClick={incluirLinea}
                      className="w-full py-2.5 border-2 border-green-500 text-green-700 rounded-lg hover:bg-green-50 font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <Plus size={18} />
                      Incluir Línea
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Confirmar */}
          {step === 3 && (
            <div className="space-y-6">
              <h3 className="font-semibold text-lg text-gray-900">Confirmar Factura</h3>

              {/* Proveedor Summary */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="text-sm font-medium text-gray-500 mb-2">Proveedor</h4>
                <div className="text-lg font-medium text-gray-900">
                  {proveedorMode === 'existing' ? selectedProveedor?.nombre : newProveedor.nombre}
                </div>
                <div className="text-sm text-gray-600">
                  {proveedorMode === 'existing' ? selectedProveedor?.identificacion : newProveedor.identificacion}
                </div>
              </div>

              {/* Lineas Summary */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">Detalle ({lineas.length} lineas)</h4>
                <div className="border rounded-lg divide-y">
                  {lineas.map((linea, index) => (
                    <div key={index} className="p-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">{linea.detalle}</div>
                        <div className="text-xs text-gray-500">
                          {linea.cantidad} x {formatMoneyModal(linea.precioUnitario)} | IVA {linea.impuestoTarifa}%
                        </div>
                      </div>
                      <div className="text-sm font-medium">
                        {formatMoneyModal((parseFloat(linea.cantidad) || 0) * (parseFloat(linea.precioUnitario) || 0) * (1 + (parseFloat(linea.impuestoTarifa) || 0) / 100))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className="p-4 border-2 border-gray-300 rounded-lg">
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-gray-600">Gravado:</span>
                  <span className="text-gray-900">{formatMoneyModal(totales.totalGravado)}</span>
                </div>
                {totales.totalExento > 0 && (
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600">Exento:</span>
                    <span className="text-gray-900">{formatMoneyModal(totales.totalExento)}</span>
                  </div>
                )}
                <div className="flex justify-between py-1 text-sm">
                  <span className="text-gray-600">IVA:</span>
                  <span className="text-gray-900">{formatMoneyModal(totales.totalImpuesto)}</span>
                </div>
                <div className="flex justify-between py-2 border-t border-gray-300 mt-2 text-lg font-bold">
                  <span className="text-gray-900">TOTAL:</span>
                  <span className="text-gray-900">{formatMoneyModal(totales.totalComprobante)}</span>
                </div>
              </div>

              {/* Tipo de FEC */}
              {fecSoloCredito && (
                <div className="p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-blue-200 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 font-bold text-sm">$</span>
                    </div>
                    <div>
                      <div className="font-semibold text-blue-900">IVA Transfronterizo - Solo Crédito</div>
                      <p className="text-sm text-blue-700 mt-1">
                        Esta FEC generará únicamente <strong>Crédito Fiscal</strong> porque el banco ya retuvo el IVA.
                        No se generará Débito Fiscal (efecto neto: reducción de IVA a pagar).
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className={`p-4 rounded-lg border ${fecSoloCredito ? 'bg-emerald-50 border-emerald-200' : 'bg-purple-50 border-purple-200'}`}>
                <p className={`text-sm ${fecSoloCredito ? 'text-emerald-800' : 'text-purple-800'}`}>
                  {fecSoloCredito ? (
                    <>
                      <strong>Efecto Fiscal:</strong> Solo Crédito Fiscal de <strong>{formatMoneyModal(totales.totalImpuesto)}</strong> (el banco ya retuvo el IVA como Débito).
                    </>
                  ) : (
                    <>
                      <strong>Efecto Fiscal:</strong> Débito + Crédito de <strong>{formatMoneyModal(totales.totalImpuesto)}</strong> cada uno (efecto neto ₡0).
                    </>
                  )}
                </p>
              </div>

              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  Al confirmar, se generará el XML, se firmará digitalmente y se enviará a Hacienda
                  para su aprobación.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <div>
            {step > 1 && (
              <button
                onClick={() => setStep(step - 1)}
                className="px-4 py-2 border rounded-lg hover:bg-gray-100"
              >
                Atras
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-lg hover:bg-gray-100"
            >
              Cancelar
            </button>
            {step < 3 ? (
              <button
                onClick={() => {
                  // Validate current step
                  if (step === 1) {
                    const prov = proveedorMode === 'existing' ? selectedProveedor : newProveedor;
                    if (!prov || !prov.identificacion || !prov.nombre) {
                      toast.error('Complete los datos del proveedor');
                      return;
                    }
                  }
                  if (step === 2) {
                    if (lineas.length === 0) {
                      toast.error('Debe agregar al menos una línea');
                      return;
                    }
                  }
                  setStep(step + 1);
                }}
                className="px-4 py-2 border-2 border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50"
              >
                Siguiente
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-6 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <CheckCircle size={18} />
                )}
                {submitting ? 'Enviando...' : 'Enviar a Hacienda'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* CABYS Explorer Modal */}
      <CabysExplorer
        isOpen={showCabysExplorer}
        onClose={() => setShowCabysExplorer(false)}
        onSelect={handleSelectCabys}
      />
    </div>
  );
}

// =============================================================================
// IMPORTAR FEC DESDE PDF
// =============================================================================
function ImportarFECModal({ onClose, onSuccess }) {
  const STEPS = ['Subir PDF', 'Proveedor', 'Líneas', 'Datos Factura', 'Resumen'];
  const [step, setStep] = useState(1);

  // Step 1: Upload
  const [file, setFile] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [pdfServerPath, setPdfServerPath] = useState(null);
  // Provider del PDF subido (local o azure_blob) — debe propagarse al crear la FEC
  // para que el endpoint que sirve el PDF sepa dónde buscarlo. Sin esto, defaultea
  // 'local' aunque el archivo realmente esté en Azure → 404 al ver el PDF.
  const [pdfServerProvider, setPdfServerProvider] = useState(null);
  const [extracting, setExtracting] = useState(false);
  // Un PDF puede traer varias facturas: se emite una FEC por cada una, en orden
  const [facturasPdf, setFacturasPdf] = useState([]);
  const [indiceFactura, setIndiceFactura] = useState(0);
  const [emitidas, setEmitidas] = useState({}); // índice → consecutivo de la FEC emitida
  // Alerta de validación: el receptor del PDF NO coincide con el emisor activo (evita el error
  // clásico de registrar una FEC en la empresa equivocada). null = OK; objeto = mostrar warning.
  const [receptorAlerta, setReceptorAlerta] = useState(null);

  // Step 2: Proveedor
  const [proveedorMode, setProveedorMode] = useState('new');
  const [selectedProveedor, setSelectedProveedor] = useState(null);
  const [newProveedor, setNewProveedor] = useState({
    tipoIdentificacion: '02', identificacion: '', nombre: '', nombreComercial: '',
    email: '', pais: 'CRI', direccion: ''
  });
  const [proveedores, setProveedores] = useState([]);
  const [searchProveedor, setSearchProveedor] = useState('');
  const [loadingProveedores, setLoadingProveedores] = useState(false);
  const [consultandoHacienda, setConsultandoHacienda] = useState(false);

  // Step 3: Lineas
  const [lineas, setLineas] = useState([]);
  const [showCabysExplorer, setShowCabysExplorer] = useState(false);
  const [cabysEditIndex, setCabysEditIndex] = useState(-1);
  const [categorias, setCategorias] = useState(null);

  // Step 4: Datos factura
  const [fechaEmision, setFechaEmision] = useState(dayjs().format('YYYY-MM-DD'));
  const [numeroFacturaProveedor, setNumeroFacturaProveedor] = useState('');
  const [condicionVenta, setCondicionVenta] = useState('01');
  const [plazoCredito, setPlazoCredito] = useState('30');
  const [medioPago, setMedioPago] = useState('02');
  const [moneda, setMoneda] = useState('USD');
  const [tipoCambio, setTipoCambio] = useState('1');
  const [fecSoloCredito, setFecSoloCredito] = useState(false);

  // Misc
  const [submitting, setSubmitting] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [aiPromptText, setAiPromptText] = useState('');
  const [aiPromptLoading, setAiPromptLoading] = useState(false);

  // Load categorias on mount
  useEffect(() => {
    gastosApi.getCategorias?.()
      .then(res => { if (res.data?.data) setCategorias(res.data.data); })
      .catch(() => {});
  }, []);

  // Auto-fetch exchange rate when moneda or fecha changes
  useEffect(() => {
    if (moneda === 'USD') {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      fetch(`${apiUrl}/internal/exchange-rate/${fechaEmision}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'X-Tenant-Slug': localStorage.getItem('tenantSlug')
        }
      })
        .then(r => r.json())
        .then(data => { if (data?.success && data?.venta) setTipoCambio(String(data.venta)); })
        .catch(() => {});
    } else {
      setTipoCambio('1');
    }
  }, [moneda, fechaEmision]);

  // Search proveedores
  useEffect(() => {
    if (!searchProveedor || searchProveedor.length < 2) { setProveedores([]); return; }
    const t = setTimeout(async () => {
      setLoadingProveedores(true);
      try {
        const res = await gastosApi.listProveedores({ q: searchProveedor, limit: 10 });
        setProveedores(res.data?.data || []);
      } catch { setProveedores([]); }
      setLoadingProveedores(false);
    }, 300);
    return () => clearTimeout(t);
  }, [searchProveedor]);

  // Carga en el formulario una de las facturas extraídas del PDF
  const aplicarFactura = async (d) => {
    setProveedorMode('new');
    setSelectedProveedor(null);
    setLineas([]);
    setNumeroFacturaProveedor('');
    setDuplicateWarning(null);

    // Pre-fill proveedor
    if (d.proveedor) {
      const p = d.proveedor;
      setNewProveedor({
        tipoIdentificacion: p.tipoIdentificacion || '02',
        identificacion: p.identificacion || '',
        nombre: p.nombre || '',
        nombreComercial: '',
        email: p.email || '',
        pais: p.pais || 'CRI',
        direccion: p.direccion || ''
      });
      // Try to find existing proveedor
      if (p.identificacion) {
        try {
          const pRes = await gastosApi.listProveedores({ q: p.identificacion, limit: 5 });
          const found = (pRes.data?.data || []).find(pv =>
            pv.identificacion === p.identificacion
          );
          if (found) {
            setProveedorMode('existing');
            setSelectedProveedor(found);
          }
        } catch {}
      }
    }

    // Pre-fill lineas
    if (d.lineas && d.lineas.length > 0) {
      // For foreign providers (tipo 05 or non-CRI), default IVA to 13% (IVA transfronterizo)
      const esExtranjero = d.proveedor?.tipoIdentificacion === '05'
        || (d.proveedor?.pais && d.proveedor.pais !== 'CRI' && d.proveedor.pais !== 'CR');
      const ivaDefault = esExtranjero ? 13 : 13;

      setLineas(d.lineas.map((l, i) => {
        const ivaFromPdf = parseFloat(l.impuestoPorcentaje);
        // If PDF has 0% tax but provider is foreign, use 13% (IVA transfronterizo required by Hacienda)
        const ivaTarifa = (esExtranjero && (isNaN(ivaFromPdf) || ivaFromPdf === 0)) ? 13 : (isNaN(ivaFromPdf) ? ivaDefault : ivaFromPdf);

        // Use precioNeto (NET PRICE) as source of truth
        const qty = parseFloat(l.cantidad) || 1;
        const unitPrice = parseFloat(l.precioUnitario) || 0;
        const netPrice = parseFloat(l.precioNeto) || 0;
        const calculated = Math.round(qty * unitPrice * 100) / 100;

        // If qty × unitPrice matches netPrice, keep both. Otherwise pro-rated: qty=1, price=netPrice
        let finalQty, finalPrice;
        if (netPrice > 0 && Math.abs(calculated - netPrice) > 0.01) {
          // Pro-rated line: use netPrice directly
          finalQty = 1;
          finalPrice = netPrice;
        } else {
          finalQty = qty;
          finalPrice = unitPrice;
        }

        return {
          detalle: l.detalle || '',
          cantidad: String(finalQty),
          precioUnitario: String(finalPrice),
          impuestoTarifa: String(ivaTarifa),
          esServicio: l.esServicio !== false,
          codigoCabys: l.cabys_sugerido?.codigo || '',
          descripcionCabys: l.cabys_sugerido?.descripcion || '',
          cabysConfianza: l.cabys_sugerido?.confianza || 'baja',
          tipo_movimiento: 'C',
          clase: 'G',
          identificacion: 'E',
          unidadMedida: 'Unid'
        };
      }));

    }

    // Pre-fill factura data
    if (d.factura) {
      if (d.factura.fechaEmision) setFechaEmision(d.factura.fechaEmision);
      if (d.factura.numero) setNumeroFacturaProveedor(d.factura.numero);
      if (d.factura.condicionVenta) {
        setCondicionVenta(d.factura.condicionVenta === 'credito' ? '02' : '01');
      }
      if (d.factura.moneda) setMoneda(d.factura.moneda);
    }

    // Save the server-side PDF path for later
    if (d._pdfStoragePath) setPdfServerPath(d._pdfStoragePath);
    if (d._pdfStorageProvider) setPdfServerProvider(d._pdfStorageProvider);

    // Alerta de receptor: si el PDF era para OTRA empresa, mostrar warning visible
    setReceptorAlerta(d._receptorAlerta || null);
  };

  // Si ya se emitió alguna FEC de este PDF, al cerrar se recarga la lista
  const cerrar = () => (Object.keys(emitidas).length > 0 ? onSuccess() : onClose());

  const irAFactura = async (i) => {
    if (i === indiceFactura || emitidas[i]) return;
    setIndiceFactura(i);
    await aplicarFactura(facturasPdf[i]);
  };

  // ========= Step 1: Extract PDF ==========
  const handleExtract = async () => {
    if (!file) return toast.error('Seleccione un archivo PDF');
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('pdf', file);
      const res = await gastosApi.extractFECPdf(formData);
      const d = res.data?.data;
      if (!d) throw new Error('Sin datos');

      const lista = Array.isArray(d.facturas) && d.facturas.length > 0 ? d.facturas : [d];
      setFacturasPdf(lista);
      setEmitidas({});
      setIndiceFactura(0);
      await aplicarFactura(lista[0]);
      if (lista.length > 1) toast.success(`El PDF trae ${lista.length} facturas: se emite una FEC por cada una`);
      toast.success('Datos extraídos exitosamente');
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message || 'Error al extraer datos del PDF');
    } finally {
      setExtracting(false);
    }
  };

  // ========= Step 2: Consultar Hacienda ==========
  const consultarHacienda = async () => {
    if (!newProveedor.identificacion) return;
    setConsultandoHacienda(true);
    try {
      const res = await gastosApi.consultarProveedorHacienda(newProveedor.identificacion);
      if (res.data?.success && res.data?.data) {
        setNewProveedor(prev => ({
          ...prev,
          nombre: res.data.data.nombre || prev.nombre,
          tipoIdentificacion: res.data.data.tipoIdentificacion || prev.tipoIdentificacion
        }));
        toast.success('Datos obtenidos de Hacienda');
      }
    } catch { toast.error('No se pudo consultar Hacienda'); }
    setConsultandoHacienda(false);
  };

  // ========= Step 3: CABYS selection ==========
  const handleSelectCabys = (cabys) => {
    if (cabysEditIndex < 0) return;
    const impuestoNum = parseFloat(cabys.impuesto);
    const updatedLineas = [...lineas];
    const originalDesc = updatedLineas[cabysEditIndex].detalle;
    const originalCabys = updatedLineas[cabysEditIndex].codigoCabys;

    updatedLineas[cabysEditIndex] = {
      ...updatedLineas[cabysEditIndex],
      codigoCabys: cabys.codigo,
      descripcionCabys: cabys.descripcion,
      impuestoTarifa: !isNaN(impuestoNum) && impuestoNum > 0 ? impuestoNum.toString() : '13',
      cabysConfianza: 'alta' // User confirmed
    };
    setLineas(updatedLineas);
    setShowCabysExplorer(false);

    // Save mapping for AI learning (if changed from original suggestion)
    if (cabys.codigo !== originalCabys && originalDesc) {
      gastosApi.saveCabysMapping({
        descripcionOriginal: originalDesc,
        cabysCodigo: cabys.codigo,
        cabysDescripcion: cabys.descripcion,
        impuestoTarifa: cabys.impuesto,
        esServicio: updatedLineas[cabysEditIndex].esServicio
      }).catch(() => {}); // Best effort
    }
  };

  const addLinea = () => {
    setLineas([...lineas, {
      detalle: '', cantidad: '1', precioUnitario: '', impuestoTarifa: '13',
      esServicio: true, codigoCabys: '', descripcionCabys: '', cabysConfianza: 'baja',
      tipo_movimiento: 'C', clase: 'G', identificacion: 'E', unidadMedida: 'Unid'
    }]);
  };

  const removeLinea = (idx) => {
    if (lineas.length <= 1) return toast.error('Debe haber al menos una línea');
    setLineas(lineas.filter((_, i) => i !== idx));
  };

  const updateLinea = (idx, field, value) => {
    const updated = [...lineas];
    updated[idx] = { ...updated[idx], [field]: value };
    setLineas(updated);
  };

  // ========= Totals ==========
  const calcularTotales = () => {
    let totalGravado = 0, totalExento = 0, totalImpuesto = 0;
    lineas.forEach(l => {
      const subtotal = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precioUnitario) || 0);
      const tarifa = parseFloat(l.impuestoTarifa) || 0;
      const imp = subtotal * (tarifa / 100);
      if (tarifa > 0) { totalGravado += subtotal; totalImpuesto += imp; }
      else { totalExento += subtotal; }
    });
    return { totalGravado, totalExento, totalImpuesto, totalComprobante: totalGravado + totalExento + totalImpuesto };
  };

  // ========= Submit ==========
  const handleSubmit = async () => {
    // Validate all lines have CABYS
    const missingCabys = lineas.some(l => !l.codigoCabys);
    if (missingCabys) return toast.error('Todas las líneas deben tener un código CABYS');

    setSubmitting(true);
    try {
      const data = {
        proveedorId: proveedorMode === 'existing' ? selectedProveedor?.id : null,
        proveedor: proveedorMode === 'new' ? newProveedor : null,
        lineas: lineas.map(l => {
          const subtotal = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precioUnitario) || 0);
          const tarifaIva = parseFloat(l.impuestoTarifa) || 0;
          const ivaTotal = subtotal * (tarifaIva / 100);
          const esIdentificado = l.identificacion === 'E';
          return {
            codigoCabys: l.codigoCabys, detalle: l.detalle,
            cantidad: l.cantidad, unidadMedida: l.unidadMedida || 'Unid',
            precioUnitario: l.precioUnitario, impuestoTarifa: l.impuestoTarifa,
            esServicio: l.esServicio,
            tipo_movimiento: l.tipo_movimiento, clase: l.clase, identificacion: l.identificacion,
            tarifa_iva_categoria: tarifaIva.toString(),
            iva_acreditar: esIdentificado ? ivaTotal : 0,
            iva_gasto: esIdentificado ? 0 : ivaTotal
          };
        }),
        fechaEmision, numeroFacturaProveedor, condicionVenta,
        plazoCredito: condicionVenta === '02' ? plazoCredito : null,
        medioPago, moneda, tipoCambio, fecSoloCredito,
        pdfStoragePath: pdfServerPath,
        pdfStorageProvider: pdfServerProvider
      };

      const res = await gastosApi.createFEC(data);
      if (res.data?.success) {
        toast.success(`FEC enviada a Hacienda. Consecutivo: ${res.data.consecutivo}`);
        const hechas = { ...emitidas, [indiceFactura]: res.data.consecutivo };
        setEmitidas(hechas);
        const siguiente = facturasPdf.findIndex((_, i) => !hechas[i]);
        if (facturasPdf.length > 1 && siguiente >= 0) {
          setIndiceFactura(siguiente);
          await aplicarFactura(facturasPdf[siguiente]);
          toast(`Sigue la factura ${siguiente + 1} de ${facturasPdf.length}`);
          return;
        }
        onSuccess();
      } else {
        toast.error(res.data?.error || 'Error al crear FEC');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al emitir FEC');
    } finally {
      setSubmitting(false);
    }
  };

  // ========= Navigation ==========
  const canNext = () => {
    if (step === 1) return !!file;
    if (step === 2) return proveedorMode === 'existing' ? !!selectedProveedor : (!!newProveedor.identificacion && !!newProveedor.nombre);
    if (step === 3) return lineas.length > 0 && lineas.every(l => l.codigoCabys && l.detalle && parseFloat(l.precioUnitario) > 0);
    if (step === 4) return true;
    return true;
  };

  const totals = calcularTotales();
  const fmt = (n) => new Intl.NumberFormat('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  const confianzaColors = { alta: 'text-green-700 bg-green-50 border-green-200', media: 'text-yellow-700 bg-yellow-50 border-yellow-200', baja: 'text-red-700 bg-red-50 border-red-200' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-xl shadow-xl w-full max-h-[90vh] flex flex-col ${step === 3 ? 'max-w-[95vw]' : 'max-w-4xl'}`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importar FEC desde PDF</h2>
            <p className="text-xs text-gray-500">Paso {step} de {STEPS.length}: {STEPS[step - 1]}</p>
          </div>
          <button onClick={cerrar} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 pt-4 pb-2">
          <div className="flex items-center gap-1">
            {STEPS.map((s, i) => (
              <div key={i} className="flex items-center flex-1">
                <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${i + 1 <= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</div>
                <span className={`ml-1 text-xs hidden md:inline ${i + 1 <= step ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>{s}</span>
                {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i + 1 < step ? 'bg-blue-600' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* STEP 1: Upload */}
          {step === 1 && (
            <div className="space-y-6">
              <div className="border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-blue-400 transition-colors">
                <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 mb-4">Seleccione la factura en PDF para extraer los datos con IA</p>
                <input type="file" accept=".pdf" onChange={(e) => {
                  const f = e.target.files[0];
                  setFile(f);
                  if (f) setPdfBlobUrl(URL.createObjectURL(f));
                }} className="mx-auto block text-sm" />
                {file && <p className="mt-3 text-sm text-blue-600 font-medium">{file.name} ({(file.size / 1024).toFixed(0)} KB)</p>}
              </div>
              <button
                onClick={handleExtract}
                disabled={!file || extracting}
                className="w-full py-3 border-2 border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50 hover:border-blue-600 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
              >
                {extracting ? <><Loader2 className="h-5 w-5 animate-spin" /> Extrayendo datos con IA...</> : <><Send className="h-5 w-5" /> Extraer con IA</>}
              </button>
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
                <div>
                  <p className="font-medium mb-1">Nota</p>
                  <p>Se usará IA para analizar el PDF y extraer automáticamente proveedor, líneas, montos y sugerir códigos CABYS.</p>
                </div>
                <button
                  type="button"
                  onClick={async () => {
                    setAiPromptLoading(true);
                    try {
                      const res = await gastosApi.getAiPrompt();
                      setAiPromptText(res.data?.data?.prompt || '');
                    } catch { setAiPromptText(''); }
                    setAiPromptLoading(false);
                    setShowAiPrompt(true);
                  }}
                  className="flex-shrink-0 ml-3 flex items-center gap-1 px-3 py-1.5 border border-blue-400 text-blue-600 rounded-lg text-xs hover:bg-blue-100"
                  title="Personalizar instrucciones de extracción IA"
                >
                  <Settings className="h-3.5 w-3.5" /> Personalizar IA
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Proveedor */}
          {step === 2 && (
            <div className="space-y-4">
              {facturasPdf.length > 1 && (
                <div className="p-3 rounded-lg border border-indigo-200 bg-indigo-50">
                  <div className="text-sm font-semibold text-indigo-900 mb-2">
                    Este PDF trae {facturasPdf.length} facturas · se emite una FEC por cada una ({Object.keys(emitidas).length} de {facturasPdf.length} emitidas)
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {facturasPdf.map((f, i) => {
                      const hecha = emitidas[i];
                      const actual = i === indiceFactura;
                      return (
                        <button
                          key={i}
                          type="button"
                          onClick={() => irAFactura(i)}
                          disabled={!!hecha || submitting}
                          className={`px-3 py-1.5 rounded-lg border text-xs font-medium whitespace-nowrap transition-colors ${
                            hecha ? 'border-green-400 text-green-700 bg-white cursor-default'
                              : actual ? 'border-indigo-600 text-indigo-700 bg-white ring-2 ring-indigo-300'
                                : 'border-gray-300 text-gray-700 bg-white hover:border-indigo-400'}`}
                          title={hecha ? `Emitida: FEC ${hecha}` : 'Cargar esta factura en el formulario'}
                        >
                          {hecha ? '✓ ' : ''}{i + 1}. {f?.factura?.numero || 'Sin número'} · {f?.proveedor?.nombre ? String(f.proveedor.nombre).slice(0, 28) : 'Proveedor'}
                          {f?.totales?.total != null ? ` · ${Number(f.totales.total).toLocaleString('es-CR', { minimumFractionDigits: 2 })} ${f?.factura?.moneda || ''}` : ''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {/* Alerta receptor: el PDF está dirigido a OTRA empresa. Visible y bloqueante hasta confirmar. */}
              {receptorAlerta && receptorAlerta.tipo === 'mismatch' && (
                <div className="rounded-xl border-2 border-red-300 bg-red-50 p-4">
                  <div className="flex items-start gap-3">
                    <div className="shrink-0 mt-0.5">
                      <AlertCircle className="h-6 w-6 text-red-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-red-800 font-semibold mb-1">⚠️ El PDF NO es para la empresa activa</h4>
                      <p className="text-sm text-red-700 mb-3">{receptorAlerta.mensaje}</p>
                      <div className="grid grid-cols-2 gap-3 mb-3 text-xs">
                        <div className="bg-white rounded-lg p-2 border border-red-200">
                          <p className="text-gray-500 uppercase tracking-wider mb-1">Empresa activa</p>
                          <p className="font-semibold text-gray-900">{receptorAlerta.emisorActivo?.nombre || '-'}</p>
                          {receptorAlerta.emisorActivo?.identificacion && (
                            <p className="text-gray-500 font-mono mt-0.5">{receptorAlerta.emisorActivo.identificacion}</p>
                          )}
                        </div>
                        <div className="bg-white rounded-lg p-2 border border-red-200">
                          <p className="text-gray-500 uppercase tracking-wider mb-1">Receptor del PDF</p>
                          <p className="font-semibold text-gray-900">{receptorAlerta.receptorPdf?.nombre || '(no detectado)'}</p>
                          {receptorAlerta.receptorPdf?.identificacion && (
                            <p className="text-gray-500 font-mono mt-0.5">{receptorAlerta.receptorPdf.identificacion}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => { setStep(1); setReceptorAlerta(null); }}
                          className="px-4 py-1.5 border-2 border-red-500 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100"
                        >
                          Volver y cambiar de empresa
                        </button>
                        <button
                          type="button"
                          onClick={() => setReceptorAlerta(null)}
                          className="px-4 py-1.5 border border-gray-300 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50"
                        >
                          Continuar de todos modos
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              {receptorAlerta && receptorAlerta.tipo === 'sin-receptor' && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-amber-800">{receptorAlerta.mensaje}</p>
                  </div>
                  <button onClick={() => setReceptorAlerta(null)} className="text-amber-600 hover:text-amber-800 text-xs font-medium">Entendido</button>
                </div>
              )}

              <div className="flex gap-4 mb-4">
                <button onClick={() => setProveedorMode('existing')} className={`px-4 py-2 rounded-lg text-sm font-medium border ${proveedorMode === 'existing' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-600'}`}>Proveedor Existente</button>
                <button onClick={() => setProveedorMode('new')} className={`px-4 py-2 rounded-lg text-sm font-medium border ${proveedorMode === 'new' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'border-gray-300 text-gray-600'}`}>Nuevo Proveedor</button>
              </div>

              {proveedorMode === 'existing' ? (
                <div className="space-y-3">
                  <div className="relative">
                    <input type="text" value={searchProveedor} onChange={(e) => setSearchProveedor(e.target.value)} placeholder="Buscar por nombre o identificación..." className="w-full px-3 py-2 border rounded-lg" />
                    {loadingProveedores && <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-gray-400" />}
                  </div>
                  {proveedores.length > 0 && (
                    <div className="border rounded-lg max-h-40 overflow-y-auto">
                      {proveedores.map(p => (
                        <button key={p.id} onClick={() => { setSelectedProveedor(p); setProveedores([]); setSearchProveedor(''); }}
                          className={`w-full text-left px-3 py-2 hover:bg-blue-50 text-sm border-b last:border-b-0 ${selectedProveedor?.id === p.id ? 'bg-blue-50' : ''}`}>
                          <span className="font-medium">{p.nombre}</span> <span className="text-gray-500">({p.identificacion})</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedProveedor && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-green-800">{selectedProveedor.nombre}</p>
                      <p className="text-xs text-green-600">{selectedProveedor.identificacion} {selectedProveedor.email ? `• ${selectedProveedor.email}` : ''}</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Tipo ID</label>
                      <select value={newProveedor.tipoIdentificacion} onChange={(e) => setNewProveedor({ ...newProveedor, tipoIdentificacion: e.target.value })} className="w-full px-2 py-2 border rounded-lg text-sm">
                        {tipoIdOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Identificación *</label>
                      <div className="flex gap-2">
                        <input type="text" value={newProveedor.identificacion} onChange={(e) => setNewProveedor({ ...newProveedor, identificacion: e.target.value })} className="flex-1 px-2 py-2 border rounded-lg text-sm" placeholder="Número de identificación" />
                        {['01','02','03','04'].includes(newProveedor.tipoIdentificacion) && (
                          <button onClick={consultarHacienda} disabled={consultandoHacienda || !newProveedor.identificacion} className="px-3 py-2 border border-blue-400 text-blue-600 rounded-lg text-xs hover:bg-blue-50 disabled:opacity-50">
                            {consultandoHacienda ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Consultar'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Razón Social *</label>
                    <input type="text" value={newProveedor.nombre} onChange={(e) => setNewProveedor({ ...newProveedor, nombre: e.target.value })} className="w-full px-2 py-2 border rounded-lg text-sm" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                      <input type="email" value={newProveedor.email} onChange={(e) => setNewProveedor({ ...newProveedor, email: e.target.value })} className="w-full px-2 py-2 border rounded-lg text-sm" />
                    </div>
                    {newProveedor.tipoIdentificacion === '05' && (
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">País</label>
                        <input type="text" value={newProveedor.pais} onChange={(e) => setNewProveedor({ ...newProveedor, pais: e.target.value })} className="w-full px-2 py-2 border rounded-lg text-sm" placeholder="CRI, USA, ESP..." />
                      </div>
                    )}
                  </div>
                  {newProveedor.tipoIdentificacion === '05' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Dirección (mín. 5 caracteres) *</label>
                      <input type="text" value={newProveedor.direccion} onChange={(e) => setNewProveedor({ ...newProveedor, direccion: e.target.value })} className="w-full px-2 py-2 border rounded-lg text-sm" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Líneas (split-screen: PDF left, lines right) */}
          {step === 3 && (
            <div className="flex gap-4 h-full">
              {/* PDF Viewer - Left */}
              {pdfBlobUrl && (
                <div className="w-1/2 flex-shrink-0 border rounded-lg overflow-hidden bg-gray-100">
                  <iframe src={pdfBlobUrl} className="w-full h-[65vh]" title="PDF importado" />
                </div>
              )}
              {/* Lines - Right */}
              <div className={`${pdfBlobUrl ? 'w-1/2' : 'w-full'} overflow-y-auto max-h-[65vh] space-y-4`}>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700 flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Los códigos CABYS fueron sugeridos por IA. Por favor <strong>verifique que cada código corresponda</strong> al producto o servicio correcto antes de continuar. Un CABYS incorrecto causará rechazo de Hacienda.</span>
              </div>

              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">{lineas.length} línea{lineas.length !== 1 ? 's' : ''}</h4>
                <button onClick={addLinea} className="text-xs px-3 py-1.5 border border-blue-400 text-blue-600 rounded-lg hover:bg-blue-50">+ Agregar línea</button>
              </div>

              {lineas.map((linea, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3 relative">
                  {lineas.length > 1 && (
                    <button onClick={() => removeLinea(idx)} className="absolute top-2 right-2 p-1 hover:bg-red-50 rounded"><X className="h-4 w-4 text-red-400" /></button>
                  )}
                  <div className="text-xs font-medium text-gray-400">Línea {idx + 1}</div>

                  {/* CABYS */}
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Código CABYS *</label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <button onClick={() => { setCabysEditIndex(idx); setShowCabysExplorer(true); }}
                          className={`w-full text-left px-3 py-2 border rounded-lg text-sm ${linea.codigoCabys ? confianzaColors[linea.cabysConfianza] || 'bg-gray-50' : 'border-red-300 bg-red-50 text-red-600'}`}>
                          {linea.codigoCabys
                            ? <><span className="font-mono">{linea.codigoCabys}</span> <span className="text-xs ml-1">— {linea.descripcionCabys || ''}</span> <span className="text-xs ml-1 opacity-70">({linea.cabysConfianza})</span></>
                            : 'Seleccionar CABYS...'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Detalle + qty + precio */}
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-5">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Detalle *</label>
                      <input type="text" value={linea.detalle} onChange={(e) => updateLinea(idx, 'detalle', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Cantidad</label>
                      <input type="number" step="0.01" value={linea.cantidad} onChange={(e) => updateLinea(idx, 'cantidad', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm text-right" />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Precio Unit.</label>
                      <input type="number" step="0.01" value={linea.precioUnitario} onChange={(e) => updateLinea(idx, 'precioUnitario', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm text-right" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">IVA %</label>
                      <select value={linea.impuestoTarifa} onChange={(e) => updateLinea(idx, 'impuestoTarifa', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm">
                        <option value="0">0%</option><option value="1">1%</option><option value="2">2%</option>
                        <option value="4">4%</option><option value="8">8%</option><option value="13">13%</option>
                      </select>
                    </div>
                  </div>

                  {/* Categorización IVA */}
                  <div className="grid grid-cols-4 gap-2">
                    <div>
                      <label className="block text-xs text-gray-500">Tipo</label>
                      <select value={linea.esServicio ? 'servicio' : 'mercancia'} onChange={(e) => updateLinea(idx, 'esServicio', e.target.value === 'servicio')} className="w-full px-2 py-1 border rounded text-xs">
                        <option value="servicio">Servicio</option><option value="mercancia">Mercancía</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Movimiento</label>
                      <select value={linea.tipo_movimiento} onChange={(e) => updateLinea(idx, 'tipo_movimiento', e.target.value)} className="w-full px-2 py-1 border rounded text-xs">
                        <option value="C">C - Compras</option><option value="D">D - Devolución</option><option value="I">I - Importación</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">Clase</label>
                      <select value={linea.clase} onChange={(e) => updateLinea(idx, 'clase', e.target.value)} className="w-full px-2 py-1 border rounded text-xs">
                        <option value="G">G - Gasto</option><option value="O">O - Operativo</option><option value="A">A - Activo</option><option value="E">E - Exportación</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-500">IVA Destino</label>
                      <select value={linea.identificacion} onChange={(e) => updateLinea(idx, 'identificacion', e.target.value)} className="w-full px-2 py-1 border rounded text-xs">
                        <option value="E">E - Acreditable</option><option value="A">A - No acreditable</option><option value="C">C - Común</option>
                      </select>
                    </div>
                  </div>

                  {/* Line subtotal */}
                  <div className="text-right text-sm text-gray-600">
                    Subtotal: <span className="font-mono font-medium">{fmt((parseFloat(linea.cantidad) || 0) * (parseFloat(linea.precioUnitario) || 0))}</span>
                  </div>
                </div>
              ))}

              {/* Totals preview */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="flex justify-between"><span>Gravado:</span><span className="font-mono">{fmt(totals.totalGravado)}</span></div>
                <div className="flex justify-between"><span>Exento:</span><span className="font-mono">{fmt(totals.totalExento)}</span></div>
                <div className="flex justify-between"><span>IVA:</span><span className="font-mono">{fmt(totals.totalImpuesto)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>Total:</span><span className="font-mono">{fmt(totals.totalComprobante)}</span></div>
              </div>
              </div>
            </div>
          )}

          {/* STEP 4: Datos Factura */}
          {step === 4 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fecha Emisión *</label>
                  <input type="date" value={fechaEmision} onChange={(e) => setFechaEmision(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Número Factura Proveedor</label>
                  <input type="text" value={numeroFacturaProveedor} onChange={(e) => setNumeroFacturaProveedor(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Ej: INV-001" />
                </div>
              </div>

              {/* Condición de Venta - highlighted */}
              <div className={`p-4 rounded-lg border-2 ${condicionVenta === '02' ? 'border-amber-400 bg-amber-50 animate-glow-amber' : 'border-blue-400 bg-blue-50 animate-glow-blue'}`}>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-gray-700 mb-1">Condición de Venta *</label>
                    <select value={condicionVenta} onChange={(e) => setCondicionVenta(e.target.value)} className={`w-full px-3 py-2 border-2 rounded-lg text-sm font-medium ${condicionVenta === '02' ? 'border-amber-500 bg-white' : 'border-blue-500 bg-white'}`}>
                      {condicionVentaOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  {condicionVenta === '02' && (
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Plazo Crédito (días)</label>
                      <input type="number" value={plazoCredito} onChange={(e) => setPlazoCredito(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                    </div>
                  )}
                </div>
                <div className={`mt-2 text-xs font-medium flex items-center gap-1.5 ${condicionVenta === '02' ? 'text-amber-700' : 'text-blue-700'}`}>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {condicionVenta === '01'
                    ? 'Contado: solo se reporta a Hacienda. NO se crea cuenta por pagar.'
                    : `Crédito: se reporta a Hacienda Y se creará una cuenta por pagar a ${plazoCredito} días.`}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Medio de Pago</label>
                  <select value={medioPago} onChange={(e) => setMedioPago(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                    {medioPagoOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Moneda</label>
                  <select value={moneda} onChange={(e) => setMoneda(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="CRC">CRC - Colones</option>
                    <option value="USD">USD - Dólares</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Cambio</label>
                  <input type="number" step="0.01" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border">
                <input type="checkbox" id="fecSoloCredito" checked={fecSoloCredito} onChange={(e) => setFecSoloCredito(e.target.checked)} className="h-4 w-4 rounded text-blue-600" />
                <label htmlFor="fecSoloCredito" className="text-sm text-gray-700 cursor-pointer">
                  <span className="font-medium">IVA Transfronterizo (Solo Crédito)</span>
                  <span className="text-gray-500 ml-1">— El banco ya retuvo el IVA</span>
                </label>
              </div>

              {/* Info about contado vs credito */}
              <div className={`p-3 rounded-lg border text-sm ${condicionVenta === '01' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                {condicionVenta === '01'
                  ? 'Contado: La FEC se reportará a Hacienda. No se creará cuenta por pagar.'
                  : `Crédito: La FEC se reportará a Hacienda y al ser aceptada se creará una cuenta por pagar a ${plazoCredito} días.`}
              </div>
            </div>
          )}

          {/* STEP 5: Preview */}
          {step === 5 && (
            <div className="space-y-4">
              {/* Proveedor */}
              <div className="border rounded-lg p-4">
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Proveedor</h4>
                <p className="font-medium text-gray-900">{proveedorMode === 'existing' ? selectedProveedor?.nombre : newProveedor.nombre}</p>
                <p className="text-sm text-gray-500">{proveedorMode === 'existing' ? selectedProveedor?.identificacion : newProveedor.identificacion}</p>
              </div>

              {/* Líneas */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">#</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Detalle</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">CABYS</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Cant</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Precio</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">IVA</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {lineas.map((l, i) => {
                      const sub = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precioUnitario) || 0);
                      const iva = sub * ((parseFloat(l.impuestoTarifa) || 0) / 100);
                      return (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                          <td className="px-3 py-2">{l.detalle}</td>
                          <td className="px-3 py-2 font-mono text-xs text-gray-500">{l.codigoCabys}</td>
                          <td className="px-3 py-2 text-right">{l.cantidad}</td>
                          <td className="px-3 py-2 text-right font-mono">{fmt(parseFloat(l.precioUnitario) || 0)}</td>
                          <td className="px-3 py-2 text-right">{l.impuestoTarifa}%</td>
                          <td className="px-3 py-2 text-right font-mono font-medium">{fmt(sub + iva)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Totales */}
              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                <div className="flex justify-between"><span>Gravado:</span><span className="font-mono">{moneda} {fmt(totals.totalGravado)}</span></div>
                <div className="flex justify-between"><span>Exento:</span><span className="font-mono">{moneda} {fmt(totals.totalExento)}</span></div>
                <div className="flex justify-between"><span>IVA:</span><span className="font-mono">{moneda} {fmt(totals.totalImpuesto)}</span></div>
                <div className="flex justify-between font-bold text-base border-t pt-2 mt-2"><span>Total Comprobante:</span><span className="font-mono">{moneda} {fmt(totals.totalComprobante)}</span></div>
              </div>

              {/* Meta */}
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-gray-50 rounded p-2"><span className="text-gray-500 text-xs">Condición:</span><br /><span className="font-medium">{condicionVenta === '01' ? 'Contado' : `Crédito (${plazoCredito} días)`}</span></div>
                <div className="bg-gray-50 rounded p-2"><span className="text-gray-500 text-xs">Medio Pago:</span><br /><span className="font-medium">{medioPagoOptions.find(o => o.value === medioPago)?.label}</span></div>
                <div className="bg-gray-50 rounded p-2"><span className="text-gray-500 text-xs">Moneda / T.C.:</span><br /><span className="font-medium">{moneda} {moneda === 'USD' ? `(T/C: ${tipoCambio})` : ''}</span></div>
              </div>

              {/* CxP note */}
              <div className={`p-3 rounded-lg border text-sm font-medium ${condicionVenta === '01' ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                {condicionVenta === '01'
                  ? 'Solo se reportará a Hacienda. No se genera cuenta por pagar.'
                  : 'Al ser aceptada por Hacienda se creará automáticamente una cuenta por pagar.'}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <button onClick={step > 1 ? () => setStep(step - 1) : cerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100">
            {step > 1 ? 'Anterior' : 'Cancelar'}
          </button>
          {step > 1 && step < 5 && (
            <button onClick={async () => {
              // Validate No. Factura Proveedor duplicado before going to step 5 (search ALL, no date filter)
              if (step === 4 && numeroFacturaProveedor) {
                try {
                  const fecList = await gastosApi.listFEC({ search: numeroFacturaProveedor, limit: 20 });
                  const dup = (fecList.data?.data || []).find(f => f.numero_factura_proveedor === numeroFacturaProveedor);
                  if (dup) {
                    setDuplicateWarning(dup);
                    return;
                  }
                } catch {}
              }
              setStep(step + 1);
            }} disabled={!canNext()} className="px-6 py-2 border-2 border-blue-500 text-blue-600 rounded-lg text-sm hover:bg-blue-50 hover:border-blue-600 disabled:opacity-50">
              Siguiente
            </button>
          )}
          {step === 5 && (
            <button onClick={handleSubmit} disabled={submitting} className="px-6 py-2 border-2 border-green-500 text-green-600 rounded-lg text-sm hover:bg-green-50 hover:border-green-600 disabled:opacity-50 flex items-center gap-2">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</> : <><Send className="h-4 w-4" /> Emitir FEC</>}
            </button>
          )}
        </div>
      </div>

      {/* CABYS Explorer */}
      <CabysExplorer isOpen={showCabysExplorer} onClose={() => setShowCabysExplorer(false)} onSelect={handleSelectCabys} />

      {/* AI Prompt Customization Modal */}
      {showAiPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Personalizar Extracción IA</h3>
                <p className="text-xs text-gray-500">Instrucciones adicionales que se envían al modelo de IA al extraer datos del PDF</p>
              </div>
              <button onClick={() => setShowAiPrompt(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700">
                <p className="font-medium mb-1">Prompt de extracción IA</p>
                <p>Este es el prompt completo que se envía al modelo de IA. Puede modificarlo para mejorar la extracción según sus necesidades.</p>
              </div>
              <textarea
                value={aiPromptText}
                onChange={(e) => setAiPromptText(e.target.value)}
                rows={16}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 font-mono leading-relaxed"
              />
              <div className="flex items-center justify-between">
                <button
                  onClick={async () => {
                    try {
                      await gastosApi.saveAiPrompt(null);
                      const res = await gastosApi.getAiPrompt();
                      setAiPromptText(res.data?.data?.prompt || '');
                      toast.success('Restaurado al prompt predeterminado');
                    } catch { toast.error('Error'); }
                  }}
                  className="text-xs px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Restaurar predeterminado
                </button>
                <p className="text-xs text-gray-400">Los mapeos CABYS aprendidos se agregan automáticamente.</p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t">
              <button onClick={() => setShowAiPrompt(false)} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50">
                Cancelar
              </button>
              <button
                onClick={async () => {
                  try {
                    await gastosApi.saveAiPrompt(aiPromptText || null);
                    toast.success('Instrucciones guardadas');
                    setShowAiPrompt(false);
                  } catch { toast.error('Error al guardar'); }
                }}
                className="px-4 py-2 border-2 border-green-500 text-green-600 rounded-lg text-sm hover:bg-green-50 flex items-center gap-2"
              >
                <Save className="h-4 w-4" /> Guardar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Warning Modal */}
      {duplicateWarning && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 text-center">
              <div className="mx-auto w-14 h-14 rounded-full border-2 border-amber-400 flex items-center justify-center mb-4">
                <AlertTriangle className="h-7 w-7 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Factura Duplicada</h3>
              <p className="text-sm text-gray-600 mb-4">
                Ya existe una FEC registrada con el número de factura del proveedor:
              </p>
              <div className="border-2 border-amber-200 rounded-lg p-4 bg-amber-50 text-left space-y-2 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">No. Factura:</span>
                  <span className="font-bold text-gray-900">{numeroFacturaProveedor}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Proveedor:</span>
                  <span className="font-medium text-gray-900">{duplicateWarning.proveedor_nombre}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Consecutivo:</span>
                  <span className="font-mono text-xs text-gray-700">{duplicateWarning.numero_consecutivo}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Estado:</span>
                  <span className={`font-medium ${duplicateWarning.estado === 'aceptado' ? 'text-green-600' : 'text-gray-600'}`}>{duplicateWarning.estado || 'N/A'}</span>
                </div>
              </div>
              <p className="text-xs text-gray-500 mb-5">
                No se puede crear otra FEC con el mismo número de factura. Verifique que no esté duplicando el registro.
              </p>
              <button
                onClick={() => setDuplicateWarning(null)}
                className="px-6 py-2.5 border-2 border-amber-500 text-amber-600 rounded-lg text-sm font-medium hover:bg-amber-50 transition-colors"
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// EDITAR Y REENVIAR FEC RECHAZADA
// =============================================================================
function EditarFECModal({ fecData, onClose, onSuccess }) {
  const STEPS = ['Líneas', 'Datos Factura', 'Resumen'];
  const [step, setStep] = useState(1);

  // Pre-cargar líneas de la FEC rechazada
  const [lineas, setLineas] = useState(() =>
    (fecData.lineas || []).map(l => ({
      detalle: l.detalle || '',
      cantidad: String(l.cantidad || 1),
      precioUnitario: String(l.precio_unitario || l.precioUnitario || 0),
      impuestoTarifa: String(l.impuesto_tarifa || l.impuestoTarifa || 13),
      esServicio: true,
      codigoCabys: l.codigo_cabys || l.codigoCabys || '',
      descripcionCabys: '',
      cabysConfianza: l.codigo_cabys ? 'media' : 'baja',
      tipo_movimiento: l.tipo_movimiento || 'C',
      clase: l.clase || 'G',
      identificacion: l.identificacion || 'E',
      unidadMedida: l.unidad_medida || l.unidadMedida || 'Unid'
    }))
  );

  const [showCabysExplorer, setShowCabysExplorer] = useState(false);
  const [cabysEditIndex, setCabysEditIndex] = useState(-1);

  // Datos factura
  const [condicionVenta, setCondicionVenta] = useState(fecData.condicion_venta || '01');
  const [plazoCredito, setPlazoCredito] = useState(String(fecData.plazo_credito || 30));
  const [medioPago, setMedioPago] = useState('02');
  const [moneda, setMoneda] = useState(fecData.moneda || 'USD');
  const [tipoCambio, setTipoCambio] = useState(String(fecData.tipo_cambio || 1));
  const [fecSoloCredito, setFecSoloCredito] = useState(!!fecData.fec_solo_credito);
  const [submitting, setSubmitting] = useState(false);

  // Auto-fetch TC
  const fechaEmision = fecData.fecha_emision ? dayjs(fecData.fecha_emision).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
  useEffect(() => {
    if (moneda === 'USD') {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      fetch(`${apiUrl}/internal/exchange-rate/${fechaEmision}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'X-Tenant-Slug': localStorage.getItem('tenantSlug') }
      }).then(r => r.json()).then(data => { if (data?.success && data?.venta) setTipoCambio(String(data.venta)); }).catch(() => {});
    }
  }, [moneda]);

  const handleSelectCabys = (cabys) => {
    if (cabysEditIndex < 0) return;
    const updated = [...lineas];
    updated[cabysEditIndex] = {
      ...updated[cabysEditIndex],
      codigoCabys: cabys.codigo,
      descripcionCabys: cabys.descripcion,
      impuestoTarifa: !isNaN(parseFloat(cabys.impuesto)) && parseFloat(cabys.impuesto) > 0 ? String(parseFloat(cabys.impuesto)) : '13',
      cabysConfianza: 'alta'
    };
    setLineas(updated);
    setShowCabysExplorer(false);
    // Save mapping
    gastosApi.saveCabysMapping({
      descripcionOriginal: updated[cabysEditIndex].detalle,
      cabysCodigo: cabys.codigo, cabysDescripcion: cabys.descripcion,
      impuestoTarifa: cabys.impuesto, esServicio: updated[cabysEditIndex].esServicio
    }).catch(() => {});
  };

  const updateLinea = (idx, field, value) => {
    const updated = [...lineas];
    updated[idx] = { ...updated[idx], [field]: value };
    setLineas(updated);
  };

  const calcularTotales = () => {
    let totalGravado = 0, totalExento = 0, totalImpuesto = 0;
    lineas.forEach(l => {
      const sub = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precioUnitario) || 0);
      const tar = parseFloat(l.impuestoTarifa) || 0;
      const imp = sub * (tar / 100);
      if (tar > 0) { totalGravado += sub; totalImpuesto += imp; } else { totalExento += sub; }
    });
    return { totalGravado, totalExento, totalImpuesto, totalComprobante: totalGravado + totalExento + totalImpuesto };
  };

  const handleSubmit = async () => {
    if (lineas.some(l => !l.codigoCabys)) return toast.error('Todas las líneas deben tener CABYS');
    setSubmitting(true);
    try {
      const data = {
        lineas: lineas.map(l => {
          const sub = (parseFloat(l.cantidad) || 0) * (parseFloat(l.precioUnitario) || 0);
          const tar = parseFloat(l.impuestoTarifa) || 0;
          const iva = sub * (tar / 100);
          const esId = l.identificacion === 'E';
          return {
            codigoCabys: l.codigoCabys, detalle: l.detalle, cantidad: l.cantidad,
            unidadMedida: l.unidadMedida || 'Unid', precioUnitario: l.precioUnitario,
            impuestoTarifa: l.impuestoTarifa, esServicio: l.esServicio,
            tipo_movimiento: l.tipo_movimiento, clase: l.clase, identificacion: l.identificacion,
            tarifa_iva_categoria: tar.toString(),
            iva_acreditar: esId ? iva : 0, iva_gasto: esId ? 0 : iva
          };
        }),
        condicionVenta, plazoCredito: condicionVenta === '02' ? plazoCredito : null,
        medioPago, moneda, tipoCambio, fecSoloCredito
      };
      const res = await gastosApi.reenviarFEC(fecData.id, data);
      if (res.data?.success) {
        toast.success(`FEC reenviada a Hacienda. Consecutivo: ${res.data.consecutivo}`);
        onSuccess();
      } else {
        toast.error(res.data?.error || 'Error al reenviar');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al reenviar FEC');
    } finally {
      setSubmitting(false);
    }
  };

  const totals = calcularTotales();
  const fmt = (n) => new Intl.NumberFormat('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
  const confianzaColors = { alta: 'text-green-700 bg-green-50 border-green-200', media: 'text-yellow-700 bg-yellow-50 border-yellow-200', baja: 'text-red-700 bg-red-50 border-red-200' };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col`}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Editar y Reenviar FEC Rechazada</h2>
            <p className="text-xs text-gray-500">
              {fecData.proveedor_nombre} — Consecutivo: <span className="font-mono">{fecData.numero_consecutivo}</span>
            </p>
            {fecData.respuesta_hacienda && (
              <p className="text-xs text-red-500 mt-1">{fecData.respuesta_hacienda.substring(0, 200)}</p>
            )}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="h-5 w-5" /></button>
        </div>

        {/* Step Indicator */}
        <div className="px-6 pt-3 pb-2 flex items-center gap-1">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-center flex-1">
              <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${i + 1 <= step ? 'bg-amber-500 text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</div>
              <span className={`ml-1 text-xs hidden md:inline ${i + 1 <= step ? 'text-amber-600 font-medium' : 'text-gray-400'}`}>{s}</span>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 ${i + 1 < step ? 'bg-amber-500' : 'bg-gray-200'}`} />}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* STEP 1: Líneas */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-medium text-gray-700">{lineas.length} línea{lineas.length !== 1 ? 's' : ''}</h4>
              </div>
              {lineas.map((linea, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <div className="text-xs font-medium text-gray-400">Línea {idx + 1}</div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Código CABYS *</label>
                    <button onClick={() => { setCabysEditIndex(idx); setShowCabysExplorer(true); }}
                      className={`w-full text-left px-3 py-2 border rounded-lg text-sm ${linea.codigoCabys ? confianzaColors[linea.cabysConfianza] || 'bg-gray-50' : 'border-red-300 bg-red-50 text-red-600'}`}>
                      {linea.codigoCabys ? <><span className="font-mono">{linea.codigoCabys}</span> <span className="text-xs ml-1">— {linea.descripcionCabys || ''}</span></> : 'Seleccionar CABYS...'}
                    </button>
                  </div>
                  <div className="grid grid-cols-12 gap-2">
                    <div className="col-span-5">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Detalle *</label>
                      <input type="text" value={linea.detalle} onChange={(e) => updateLinea(idx, 'detalle', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Cantidad</label>
                      <input type="number" step="0.01" value={linea.cantidad} onChange={(e) => updateLinea(idx, 'cantidad', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm text-right" />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Precio Unit.</label>
                      <input type="number" step="0.01" value={linea.precioUnitario} onChange={(e) => updateLinea(idx, 'precioUnitario', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm text-right" />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">IVA %</label>
                      <select value={linea.impuestoTarifa} onChange={(e) => updateLinea(idx, 'impuestoTarifa', e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm">
                        <option value="0">0%</option><option value="1">1%</option><option value="2">2%</option>
                        <option value="4">4%</option><option value="8">8%</option><option value="13">13%</option>
                      </select>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    Subtotal: <span className="font-mono font-medium">{fmt((parseFloat(linea.cantidad) || 0) * (parseFloat(linea.precioUnitario) || 0))}</span>
                  </div>
                </div>
              ))}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <div className="flex justify-between"><span>Gravado:</span><span className="font-mono">{fmt(totals.totalGravado)}</span></div>
                <div className="flex justify-between"><span>IVA:</span><span className="font-mono">{fmt(totals.totalImpuesto)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1 mt-1"><span>Total:</span><span className="font-mono">{fmt(totals.totalComprobante)}</span></div>
              </div>
            </div>
          )}

          {/* STEP 2: Datos Factura */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Condición de Venta</label>
                  <select value={condicionVenta} onChange={(e) => setCondicionVenta(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                    {condicionVentaOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
                {condicionVenta === '02' && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Plazo Crédito (días)</label>
                    <input type="number" value={plazoCredito} onChange={(e) => setPlazoCredito(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Moneda</label>
                  <select value={moneda} onChange={(e) => setMoneda(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="CRC">CRC</option><option value="USD">USD</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Tipo de Cambio</label>
                  <input type="number" step="0.01" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)} className="w-full px-3 py-2 border rounded-lg text-sm" />
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Resumen */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="border rounded-lg p-4">
                <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Proveedor</h4>
                <p className="font-medium">{fecData.proveedor_nombre}</p>
                <p className="text-sm text-gray-500">{fecData.proveedor_identificacion}</p>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">#</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">Detalle</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-gray-500">CABYS</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">Precio</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-gray-500">IVA</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {lineas.map((l, i) => (
                      <tr key={i}><td className="px-3 py-2 text-gray-400">{i+1}</td><td className="px-3 py-2">{l.detalle}</td><td className="px-3 py-2 font-mono text-xs text-gray-500">{l.codigoCabys}</td><td className="px-3 py-2 text-right font-mono">{fmt(parseFloat(l.precioUnitario)||0)}</td><td className="px-3 py-2 text-right">{l.impuestoTarifa}%</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 text-sm">
                <div className="flex justify-between"><span>Gravado:</span><span className="font-mono">{moneda} {fmt(totals.totalGravado)}</span></div>
                <div className="flex justify-between"><span>IVA:</span><span className="font-mono">{moneda} {fmt(totals.totalImpuesto)}</span></div>
                <div className="flex justify-between font-bold text-base border-t pt-2 mt-2"><span>Total:</span><span className="font-mono">{moneda} {fmt(totals.totalComprobante)}</span></div>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                Se reenviará con el mismo consecutivo <span className="font-mono font-bold">{fecData.numero_consecutivo}</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t bg-gray-50">
          <button onClick={step > 1 ? () => setStep(step - 1) : onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-100">
            {step > 1 ? 'Anterior' : 'Cancelar'}
          </button>
          {step < 3 && (
            <button onClick={() => setStep(step + 1)} className="px-6 py-2 border-2 border-blue-500 text-blue-600 rounded-lg text-sm hover:bg-blue-50">Siguiente</button>
          )}
          {step === 3 && (
            <button onClick={handleSubmit} disabled={submitting} className="px-6 py-2 border-2 border-amber-500 text-amber-600 rounded-lg text-sm hover:bg-amber-50 disabled:opacity-50 flex items-center gap-2">
              {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Reenviando...</> : <><Send className="h-4 w-4" /> Reenviar FEC</>}
            </button>
          )}
        </div>
      </div>

      <CabysExplorer isOpen={showCabysExplorer} onClose={() => setShowCabysExplorer(false)} onSelect={handleSelectCabys} />
    </div>
  );
}
